import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import tvSocket from '../sockets/tvSocket';
import {
  createContent,
  createPlaylist,
  getContentStatus,
  getScreenSchedule,
  getScreenResolution,
  getUploadPolicy,
  listSectionContent,
  upsertScreenSchedule,
  uploadFileToS3,
} from '../api/contentApi';
import { createScheduleFromLegacyShape } from '../api/schedulesApi';
import { getMediaDimensions, checkAspectRatioMatch, generateThumbnail, isImageFile, isVideoFile } from '../utils/mediaValidation';
import { uploadBatch } from '../utils/batchUploader';
import { postContentUploadAnalytics } from '../api/analyticsApi';
import ScreenPreview from './ScreenPreview';
import { getActiveProjectId } from '../constants/projectConstants';
import { getScreenCategoryFromDeviceId, formatCategoryLabel, categorySortKey, UNCATEGORIZED } from '../utils/screenCategories';
import { CONTENT_CATEGORIES, getCategory } from '../utils/contentCategories';
import { getDeviceDisplayName, getDeviceCompactLabel } from '../utils/deviceLabel';
import ScheduleEventModal from './ScheduleEventModal';
import { Button } from './ui';
import { Tv2, Radio, Clock, AlertCircle, Search, X, RefreshCw, Plus, FolderOpen, Image as ImageLucide, Video as VideoLucide, Building2 } from 'lucide-react';
import MediaV2Shell from './media/MediaV2Shell';
import AssetPreviewModal from './media/AssetPreviewModal';
import { isMediaV2Enabled } from './media/mediaHelpers';
import './media.css';

// SVG Icon Components
const MediaIcon = ({ size = 20, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M21 19V5C21 3.9 20.1 3 19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19ZM8.5 13.5L11 16.51L14.5 12L19 18H5L8.5 13.5Z" fill={color} />
  </svg>
);

const CloudUploadIcon = ({ size = 20, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M19.35 10.04C18.67 6.59 15.64 4 12 4C9.11 4 6.6 5.64 5.35 8.04C2.34 8.36 0 10.91 0 14C0 17.31 2.69 20 6 20H19C21.76 20 24 17.76 24 15C24 12.36 21.95 10.22 19.35 10.04ZM14 13V17H10V13H7L12 8L17 13H14Z" fill={color} />
  </svg>
);

const SearchIcon = ({ size = 20, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M15.5 14H14.71L14.43 13.73C15.41 12.59 16 11.11 16 9.5C16 5.91 13.09 3 9.5 3C5.91 3 3 5.91 3 9.5C3 13.09 5.91 16 9.5 16C11.11 16 12.59 15.41 13.73 14.43L14 14.71V15.5L19 20.49L20.49 19L15.5 14ZM9.5 14C7.01 14 5 11.99 5 9.5C5 7.01 7.01 5 9.5 5C11.99 5 14 7.01 14 9.5C14 11.99 11.99 14 9.5 14Z" fill={color} />
  </svg>
);

const SortIcon = ({ size = 20, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M3 18H9V16H3V18ZM3 6V8H21V6H3ZM3 13H15V11H3V13Z" fill={color} />
  </svg>
);

const DropdownIcon = ({ size = 20, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M7 10L12 15L17 10H7Z" fill={color} />
  </svg>
);

const FolderIcon = ({ size = 20, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M10 4H4C2.9 4 2.01 4.9 2.01 6L2 18C2 19.1 2.9 20 4 20H20C21.1 20 22 19.1 22 18V8C22 6.9 21.1 6 20 6H12L10 4Z" fill={color} />
  </svg>
);

const GridIcon = ({ size = 20, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M4 4H10V10H4V4ZM14 4H20V10H14V4ZM4 14H10V20H4V14ZM14 14H20V20H14V14Z" fill={color} />
  </svg>
);

const ListIcon = ({ size = 20, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M3 13H5V11H3V13ZM3 17H5V15H3V17ZM3 9H5V7H3V9ZM7 13H21V11H7V13ZM7 17H21V15H7V17ZM7 7V9H21V7H7Z" fill={color} />
  </svg>
);

const FileIcon = ({ size = 48, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.89 22 5.99 22H18C19.1 22 20 21.1 20 20V8L14 2ZM16 18H8V16H16V18ZM16 14H8V12H16V14ZM13 9V3.5L18.5 9H13Z" fill={color} />
  </svg>
);

const VideoIcon = ({ size = 14, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M17 10.5V7C17 6.45 16.55 6 16 6H4C3.45 6 3 6.45 3 7V17C3 17.55 3.45 18 4 18H16C16.55 18 17 17.55 17 17V13.5L21 17.5V6.5L17 10.5Z" fill={color} />
  </svg>
);

const ImageIcon = ({ size = 14, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M21 19V5C21 3.9 20.1 3 19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19ZM8.5 13.5L11 16.51L14.5 12L19 18H5L8.5 13.5Z" fill={color} />
  </svg>
);

const ShareIcon = ({ size = 18, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M18 16.08C17.24 16.08 16.56 16.38 16.04 16.85L8.91 12.7C8.96 12.47 9 12.24 9 12C9 11.76 8.96 11.53 8.91 11.3L15.96 7.19C16.5 7.69 17.21 8 18 8C19.66 8 21 6.66 21 5C21 3.34 19.66 2 18 2C16.34 2 15 3.34 15 5C15 5.24 15.04 5.47 15.09 5.7L8.04 9.81C7.5 9.31 6.79 9 6 9C4.34 9 3 10.34 3 12C3 13.66 4.34 15 6 15C6.79 15 7.5 14.69 8.04 14.19L15.16 18.34C15.11 18.55 15.08 18.77 15.08 19C15.08 20.61 16.39 21.92 18 21.92C19.61 21.92 20.92 20.61 20.92 19C20.92 17.39 19.61 16.08 18 16.08Z" fill={color} />
  </svg>
);

const DownloadIcon = ({ size = 18, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M19 9H15V3H9V9H5L12 16L19 9ZM5 18V20H19V18H5Z" fill={color} />
  </svg>
);

const DeleteIcon = ({ size = 18, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M6 19C6 20.1 6.9 21 8 21H16C17.1 21 18 20.1 18 19V7H6V19ZM19 4H15.5L14.5 3H9.5L8.5 4H5V6H19V4Z" fill={color} />
  </svg>
);

const InfoIcon = ({ size = 16, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM13 17H11V11H13V17ZM13 9H11V7H13V9Z" fill={color} />
  </svg>
);

const StopIcon = ({ size = 16, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M6 6H18V18H6V6ZM8 8V16H16V8H8Z" fill={color} />
  </svg>
);

const UploadIcon = ({ size = 16, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M9 16H15V10H19L12 3L5 10H9V16ZM5 18V20H19V18H5Z" fill={color} />
  </svg>
);

const PlayIcon = ({ size = 24, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M8 5V19L19 12L8 5Z" fill={color} />
  </svg>
);

const AddIcon = ({ size = 24, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M19 13H13V19H11V13H5V11H11V5H13V11H19V13Z" fill={color} />
  </svg>
);

const CloseIcon = ({ size = 20, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z" fill={color} />
  </svg>
);

const RefreshIcon = ({ size = 20, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4C7.58 4 4 7.58 4 12C4 16.42 7.58 20 12 20C15.73 20 18.84 17.45 19.73 14H17.65C16.83 16.33 14.61 18 12 18C8.69 18 6 15.31 6 12C6 8.69 8.69 6 12 6C13.66 6 15.14 6.69 16.22 7.78L13 11H20V4L17.65 6.35Z" fill={color} />
  </svg>
);

const REFRESH_INTERVAL_MS = 8000;
const AUTO_SCHEDULER_INTERVAL_MS = 15000;

// Full section list — kept for backend compatibility (fetching existing content).
// The upload UI auto-assigns section from file type (images→gallery, videos→video, pdf→brochure).
const SECTION_OPTIONS = [
  { value: 'brochure', label: 'Brochures' },
  { value: 'specifications', label: 'Specifications' },
  { value: 'video', label: 'Videos' },
  { value: 'floorplan', label: 'Floorplan' },
  { value: 'masterplan', label: 'Masterplan' },
  { value: 'gallery', label: 'Images' },
  { value: 'location', label: 'Location' },
  { value: 'screensaver', label: 'Screensaver' },
];

// Sections shown in the filter dropdown (user-facing, simplified)
const FILTER_SECTIONS = [
  { value: 'images', label: 'Images' },
  { value: 'videos', label: 'Videos' },
  { value: 'brochures', label: 'Brochures' },
];

/**
 * Auto-detect section from file extension.
 * Images → gallery, Videos → video, PDFs → brochure.
 */
function autoDetectSection(fileName) {
  if (!fileName) return 'gallery';
  const ext = fileName.toLowerCase().split('.').pop();
  if (VIDEO_EXTENSIONS.some(e => e.replace('.', '') === ext)) return 'video';
  if (ext === 'pdf') return 'brochure';
  return 'gallery'; // all image formats
}

const IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.webp', '.gif', '.bmp', '.svg', '.avif'];
const VIDEO_EXTENSIONS = ['.mp4', '.webm', '.mov', '.m4v', '.mkv', '.avi'];

/**
 * Converts comma-separated tags input into array format expected by backend.
 * @param {string} tagsInput
 * @returns {string[]}
 */
const parseTags = (tagsInput) => (
  tagsInput
    .split(',')
    .map((tag) => tag.trim())
    .filter(Boolean)
);

/**
 * Infers content type from selected file and section.
 * Section takes priority for "video" so behavior stays consistent with CMS grouping.
 * @param {File|null} file
 * @param {string} section
 * @returns {'video'|'image'|'document'|'url'}
 */
const inferContentType = (file, section) => {
  if (section === 'screensaver') return 'screensaver';
  if (section === 'video') return 'video';
  if (!file?.type) return 'image';
  if (file.type.startsWith('video/')) return 'video';
  if (file.type.startsWith('image/')) return 'image';
  if (file.type.includes('pdf') || file.type.startsWith('application/')) return 'document';
  return 'image';
};

/**
 * Converts API item shape into a display-friendly shape with a resolved URL.
 * @param {Object} item
 * @param {string} section
 * @returns {Object}
 */
const normalizeContentItem = (item, section) => {
  const resolvedUrl =
    item?.cdnUrl ||
    item?.url ||
    item?.cdn_url ||
    item?.fileUrl ||
    item?.downloadUrl ||
    item?.signedUrl ||
    item?.src ||
    item?.data?.url ||
    item?.data?.cdnUrl ||
    '';
  const fileName = item?.file_name || item?.data?.fileName || item?.name || '';
  // Prefer API's content_id so playlists/schedules use the real backend ID;
  // fall through to _id/id, then name/filename as last-resort. The previous
  // order read `name` first which meant library items flowed with
  // contentId=filename and every schedule created after upload stored
  // filenames as contentIds — breaking TV-app content resolution for all
  // but the first item in auto-merged loops.
  const rawContentId =
    item?.content_id ?? item?.contentId ?? item?._id ?? item?.id ?? item?.name ?? item?.title ?? fileName;
  const contentId = String(rawContentId || '');

  const stableIdFallback = [
    section,
    contentId,
    fileName,
    item?.createdAt || '',
    item?.updatedAt || '',
    item?.key || item?.s3Key || '',
  ].join('|');

  return {
    id: item?._id || item?.content_id || item?.id || stableIdFallback,
    contentId,
    title: item?.title || item?.name || fileName || 'Untitled',
    name: item?.name || fileName || 'content',
    type: item?.type || (section === 'video' ? 'video' : 'image'),
    section,
    fileName,
    createdAt: item?.createdAt || '',
    url: resolvedUrl,
    width: item?.width || null,
    height: item?.height || null,
    status: item?.status || 'active',
    archived_at: item?.archived_at || null,
    is_deleted: item?.is_deleted || false,
    deleted_at: item?.deleted_at || null,
    folder_id: item?.folder_id || item?.folderId || null,
    category: item?.category || null,
  };
};

/**
 * Reads the file extension from a URL/path.
 * @param {string} value
 * @returns {string}
 */
const getLowerCaseExtension = (value) => {
  if (!value) return '';
  const cleanValue = String(value).split('?')[0];
  const lastDotIndex = cleanValue.lastIndexOf('.');
  if (lastDotIndex < 0) return '';
  return cleanValue.slice(lastDotIndex).toLowerCase();
};

/** Safe download filename; append extension from URL when the name has none. */
const buildDownloadFileName = (item) => {
  let base =
    item?.fileName ||
    item?.name ||
    item?.title ||
    `content-${item?.contentId || item?.id || 'file'}`;
  base = String(base).replace(/[/\\?%*:|"<>]/g, '_').trim() || 'download';
  const extFromUrl = getLowerCaseExtension(item?.url || '');
  if (extFromUrl && !base.toLowerCase().endsWith(extFromUrl)) {
    base += extFromUrl;
  }
  return base;
};

/**
 * Try fetch+blob when CORS allows; otherwise open URL in a new tab (typical for S3/CDN).
 * Cross-origin `download` is often ignored; the user still gets the file or can Save as.
 */
const downloadFromUrl = async (url, fileName) => {
  if (!url || typeof url !== 'string') return { ok: false, usedBlob: false };

  try {
    const response = await fetch(url, { mode: 'cors', credentials: 'omit', cache: 'no-store' });
    if (response.ok) {
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.setTimeout(() => URL.revokeObjectURL(blobUrl), 1500);
      return { ok: true, usedBlob: true };
    }
  } catch {
    /* CORS or network — use fallback */
  }

  try {
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    return { ok: true, usedBlob: false };
  } catch {
    window.open(url, '_blank', 'noopener,noreferrer');
    return { ok: true, usedBlob: false };
  }
};

/**
 * Determines if an item should show image preview.
 * @param {Object} item
 * @returns {boolean}
 */
const isImageItem = (item) => {
  if (item?.type === 'image') return true;
  const extension = getLowerCaseExtension(item?.url || item?.fileName || item?.name);
  return IMAGE_EXTENSIONS.includes(extension);
};

/**
 * Determines if an item should show video preview.
 * @param {Object} item
 * @returns {boolean}
 */
const isVideoItem = (item) => {
  if (item?.type === 'video' || item?.section === 'video') return true;
  const extension = getLowerCaseExtension(item?.url || item?.fileName || item?.name);
  return VIDEO_EXTENSIONS.includes(extension);
};

/**
 * Converts HH:mm string to minutes since midnight.
 * @param {string} hhmm
 * @returns {number}
 */
const timeToMinutes = (hhmm) => {
  const [hours, minutes] = String(hhmm || '').split(':').map((value) => Number(value));
  if (Number.isNaN(hours) || Number.isNaN(minutes)) return -1;
  return (hours * 60) + minutes;
};

/**
 * Checks if a time falls in a start-end range.
 * Supports overnight ranges where end < start (e.g. 23:00-01:00).
 * @param {number} nowMinutes
 * @param {string} startTime
 * @param {string} endTime
 * @returns {boolean}
 */
const isInTimeRange = (nowMinutes, startTime, endTime) => {
  const startMinutes = timeToMinutes(startTime);
  const endMinutes = timeToMinutes(endTime);

  if (startMinutes < 0 || endMinutes < 0) return false;

  if (startMinutes <= endMinutes) {
    return nowMinutes >= startMinutes && nowMinutes < endMinutes;
  }

  // Overnight schedule: e.g. 23:00 -> 01:00
  return nowMinutes >= startMinutes || nowMinutes < endMinutes;
};

/**
 * Sorts schedule slots by start time.
 * @param {Array<{startTime:string}>} schedule
 * @returns {Array}
 */
const sortSchedule = (schedule) => (
  [...schedule].sort((a, b) => timeToMinutes(a.startTime) - timeToMinutes(b.startTime))
);

/**
 * Render a single media card. Extracted from the main component so the
 * grouped-by-section layout and the flat screensaver-mode layout can share
 * exactly the same card markup without duplicating 60+ lines of JSX.
 *
 * All state (hover, selection, schedule-ness) is passed in as options; the
 * function itself is pure — it doesn't touch component state directly.
 */
function renderMediaCard(item, options) {
  const {
    isSelected,
    isHovered,
    isLive,
    onEnter,
    onLeave,
    onToggle,
    onSchedule,
    onPreview,
    videoPreviewRef,
    keyPrefix = '',
  } = options;
  const isVideo = isVideoItem(item);
  const isImage = isImageItem(item);
  const cat = item.category ? getCategory(item.category) : null;
  const chipColor = cat ? cat.color : '#9ca3af';
  const chipLabel = cat ? cat.label : 'Uncategorized';
  return (
    <div
      key={`${keyPrefix}${item.id}`}
      className={[
        'media__card',
        isSelected && 'media__card--selected',
        isLive && 'media__card--live',
      ].filter(Boolean).join(' ')}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
    >
      <div
        className="media__card-preview"
        onClick={(e) => {
          if (!onPreview) return;
          e.stopPropagation();
          onPreview(item);
        }}
        style={onPreview ? { cursor: 'zoom-in' } : undefined}
      >
        {item.url && isImage ? (
          <img src={item.url} alt={item.title || ''} loading="lazy" />
        ) : item.url && isVideo ? (
          <video
            ref={isHovered ? videoPreviewRef : null}
            src={item.url}
            muted
            loop
            playsInline
            preload="metadata"
          />
        ) : (
          <div className="media__card-empty">
            <VideoLucide size={36} />
          </div>
        )}
        <input
          type="checkbox"
          className="media__card-checkbox"
          checked={isSelected}
          onChange={onToggle}
          onClick={(e) => e.stopPropagation()}
          aria-label="Select asset"
        />
      </div>
      <div className="media__card-body">
        <h3 className="media__card-title">{item.title || 'Untitled'}</h3>
        <div
          className="media__card-category"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            fontSize: 10,
            fontWeight: 600,
            color: chipColor,
            background: `${chipColor}15`,
            padding: '2px 6px',
            borderRadius: 3,
            marginBottom: 4,
            alignSelf: 'flex-start',
          }}
        >
          <span
            style={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              background: chipColor,
            }}
          />
          {chipLabel}
        </div>
        <div className="media__card-meta-row">
          <span className="media__card-meta">
            {isVideo ? <VideoLucide size={12} /> : <ImageLucide size={12} />}
            {isVideo ? 'Video' : 'Image'}
            {item.width && item.height && (
              <span className="media__card-res">{item.width}×{item.height}</span>
            )}
          </span>
          <button
            type="button"
            className={`media__card-schedule${isLive ? ' media__card-schedule--live' : ''}`}
            onClick={onSchedule}
          >
            {isLive ? 'Live · Edit' : 'Schedule'}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * WebsiteContentUploader
 * Single-project CMS upload + playback + schedule management in one component.
 */
const WebsiteContentUploader = ({ tvs = [], selectedTV = null, screensaverOnly = false }) => {
  const [section, setSection] = useState(screensaverOnly ? 'screensaver' : 'brochure');
  const [file, setFile] = useState(null);
  const [contentType, setContentType] = useState(screensaverOnly ? 'screensaver' : 'image');
  const [title, setTitle] = useState('');
  // Marketing taxonomy — single-select per upload session, applied to every
  // file in this batch. Required at form-level (handleUpload blocks submit on
  // null). Backend accepts null during ramp; will flip to required once all
  // existing creatives are backfilled.
  const [category, setCategory] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [actionMessage, setActionMessage] = useState('');
  const [selectedTvId, setSelectedTvId] = useState(selectedTV?.tvId || '');
  const [activeLibrarySection, setActiveLibrarySection] = useState(
    screensaverOnly ? 'screensaver' : 'brochure',
  );
  const [isLibraryLoading, setIsLibraryLoading] = useState(true);
  const [lastRefreshedAt, setLastRefreshedAt] = useState(null);
  const [fileInputKey, setFileInputKey] = useState(0);
  const [isScheduleLoading, setIsScheduleLoading] = useState(false);
  const [isScheduleSaving, setIsScheduleSaving] = useState(false);
  const [screenSchedule, setScreenSchedule] = useState([]);
  const [scheduleDrafts, setScheduleDrafts] = useState({});
  // Project-wide content status (contentId → "playing"|"scheduled"|"idle"|"never_scheduled").
  // Library stat tiles read from this instead of the per-screen screenSchedule map
  // because the media view is no longer scoped to a selected TV.
  const [contentStatusByKey, setContentStatusByKey] = useState({});
  const [libraryBySection, setLibraryBySection] = useState(() => {
    const initialState = {};
    SECTION_OPTIONS.forEach((option) => {
      initialState[option.value] = [];
    });
    return initialState;
  });
  const lastAutoPlayingContentIdRef = useRef(null);
  
  // New state for media library UI
  const [activeTab, setActiveTab] = useState('mediaFiles');
  const [contentFilter, setContentFilter] = useState('images'); // 'images' | 'videos' | 'brochures'
  const [scheduleFilter, setScheduleFilter] = useState('all'); // 'all' | 'live' | 'unscheduled'
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [contextMenuOpen, setContextMenuOpen] = useState(null);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [showAddToPlaylistModal, setShowAddToPlaylistModal] = useState(false);
  const [playlistName, setPlaylistName] = useState('');
  const [playlistDescription, setPlaylistDescription] = useState('');
  const [addToPlaylistError, setAddToPlaylistError] = useState(null);
  const [addToPlaylistSuccess, setAddToPlaylistSuccess] = useState(null);
  const [addToPlaylistSaving, setAddToPlaylistSaving] = useState(false);
  const [viewMode, setViewMode] = useState('grid');
  const [filePreview, setFilePreview] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [screenSearchQuery, setScreenSearchQuery] = useState(undefined); // undefined = closed, string = open with query
  // Schedule-after-upload state removed — scheduling is now a separate flow
  // via the per-card Schedule button on the Media library.
  // Content validity / expiry
  const [isPermanent, setIsPermanent] = useState(true);
  const [validFrom, setValidFrom] = useState('');
  const [validUntil, setValidUntil] = useState('');
  // Quick-schedule modal for existing library items
  const [quickScheduleItem, setQuickScheduleItem] = useState(null); // {id, title, contentId}
  const [quickScheduleScreenIds, setQuickScheduleScreenIds] = useState([]); // multi-select screen IDs
  const [quickScheduleScreenSearch, setQuickScheduleScreenSearch] = useState('');
  const [quickScheduleStartDate, setQuickScheduleStartDate] = useState(new Date().toISOString().slice(0, 10));
  const [quickScheduleEndDate, setQuickScheduleEndDate] = useState('');
  const [quickScheduleStartTime, setQuickScheduleStartTime] = useState('');
  const [quickScheduleEndTime, setQuickScheduleEndTime] = useState('');
  const [quickScheduleSaving, setQuickScheduleSaving] = useState(false);
  const [quickScheduleMsg, setQuickScheduleMsg] = useState('');
  // Aspect ratio validation state
  const [screenResolution, setScreenResolution] = useState(null); // {width, height, aspectRatio}
  const [screenCalibration, setScreenCalibration] = useState(null); // calibration from selected device
  // Duplicate-title upload guard: null = no conflict, 'confirmed' = user ack'd, else = existing item
  const [duplicateUploadConflict, setDuplicateUploadConflict] = useState(null);

  /**
   * Return {width, height, aspectRatio} for a device, resolving through
   * its screen profile when set. The backend serializeDevice now
   * populates `profileContent` directly on every device response, so we
   * can read it without a separate profile fetch. Falls back to the
   * legacy nativeResolution for unprofiled devices. Keep this in sync
   * with deviceLabel.js's getDeviceSubtitle / getResolutionLabel — they
   * all use the same "profileContent → nativeResolution" priority.
   */
  const resolveScreenDims = useCallback((device) => {
    if (!device) return null;
    const dims = device.profileContent || device.nativeResolution;
    if (dims?.width && dims?.height) {
      return {
        width: dims.width,
        height: dims.height,
        aspectRatio: dims.aspectRatio || null,
      };
    }
    return null;
  }, []);
  const [fileDimensions, setFileDimensions] = useState(null); // {width, height}
  const [aspectRatioError, setAspectRatioError] = useState(null); // string error message or null
  // Multi-file upload state.
  // Each entry:
  // {
  //   id, file, title, mediaType,
  //   thumbnailUrl, thumbnailRevocable, thumbnailLoading, thumbnailError,
  //   dimensions, aspectRatio, aspectRatioError,
  //   uploadStatus: 'pending'|'validating'|'uploading'|'done'|'failed',
  //   uploadError, contentId
  // }
  const [pendingFiles, setPendingFiles] = useState([]);
  const pendingFilesRef = useRef([]);
  useEffect(() => { pendingFilesRef.current = pendingFiles; }, [pendingFiles]);
  const pendingIdCounterRef = useRef(0);
  const [batchProgress, setBatchProgress] = useState(null); // {completed, total} or null
  const [hoveredCard, setHoveredCard] = useState(null);
  const [previewItem, setPreviewItem] = useState(null);
  const videoPreviewRef = useRef(null);

  // Play video on card hover, pause on leave (like scheduler page cards).
  useEffect(() => {
    if (!hoveredCard) return;
    const t = setTimeout(() => {
      if (videoPreviewRef.current) {
        videoPreviewRef.current.play().catch(() => {});
      }
    }, 0);
    return () => clearTimeout(t);
  }, [hoveredCard]);

  // Keep TV selection aligned with dashboard-selected TV when available.
  useEffect(() => {
    if (selectedTV?.tvId) {
      setSelectedTvId(selectedTV.tvId);
    }
  }, [selectedTV]);

  // Auto-select first TV if none is selected so schedule actions are immediately usable.
  useEffect(() => {
    if (!selectedTvId && tvs.length > 0) {
      setSelectedTvId(tvs[0].tvId);
    }
  }, [selectedTvId, tvs]);

  // Resolve the selected screen's effective resolution. Tries the profile
  // cache first (so profile switches reflect instantly in the modal with
  // no round-trip) and falls back to the backend getScreenResolution call
  // only when the cache misses. Backend also resolves through profile now
  // (see sumadhura.screen.controller.js), so the two paths agree.
  useEffect(() => {
    if (!selectedTvId) {
      setScreenResolution(null);
      return;
    }
    // Check the local cache first — if the selected device object in
    // `tvs` has profileId and we know that profile, use it immediately.
    const selectedTv = tvs.find(
      (t) => t.tvId === selectedTvId || t.deviceId === selectedTvId
    );
    if (selectedTv) {
      const dims = resolveScreenDims(selectedTv);
      if (dims) {
        setScreenResolution(dims);
        setScreenCalibration(selectedTv.calibration || null);
        return;
      }
    }
    // Fall back to the backend lookup for devices we don't have cached.
    let cancelled = false;
    getScreenResolution(selectedTvId)
      .then((res) => {
        if (!cancelled) setScreenResolution(res?.nativeResolution || null);
      })
      .catch(() => {
        if (!cancelled) { setScreenResolution(null); setScreenCalibration(null); }
      });
    return () => { cancelled = true; };
  }, [selectedTvId, tvs, resolveScreenDims]);

  // Probe dimensions whenever a file is set — regardless of whether a
  // target screen is picked. The previous gate (`!screenResolution → bail`)
  // meant every upload without a target screen landed in the DB with null
  // width/height, forcing a server-side backfill. Dims are file-intrinsic;
  // we only defer the aspect-ratio check to when a screen is known.
  useEffect(() => {
    if (!file) {
      setAspectRatioError(null);
      setFileDimensions(null);
      return;
    }
    let cancelled = false;
    getMediaDimensions(file)
      .then((dims) => {
        if (cancelled) return;
        setFileDimensions(dims);
        if (!screenResolution) {
          setAspectRatioError(null);
          return;
        }
        const result = checkAspectRatioMatch(dims.width, dims.height, screenResolution.width, screenResolution.height);
        if (!result.valid) {
          setAspectRatioError(
            `This ${result.actualOrientation} content (${result.actual}) cannot be uploaded to a ${result.expectedOrientation} screen (${result.expected}). Upload content matching ${screenResolution.width}x${screenResolution.height}.`
          );
        } else {
          setAspectRatioError(null);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setFileDimensions(null);
          setAspectRatioError(null);
        }
      });
    return () => { cancelled = true; };
  }, [file, screenResolution]);

  // ---- Multi-file pending state helpers ----

  const updatePendingFile = useCallback((id, patch) => {
    setPendingFiles((prev) => prev.map((pf) => (pf.id === id ? { ...pf, ...patch } : pf)));
  }, []);

  const computeAspectRatioForDims = useCallback((dims, sr) => {
    if (!dims || !sr) return { aspectRatio: null, aspectRatioError: null };
    const result = checkAspectRatioMatch(dims.width, dims.height, sr.width, sr.height);
    const aspectRatioError = result.valid
      ? null
      : `${result.actualOrientation} ${result.actual} incompatible with ${result.expectedOrientation} ${result.expected}`;
    return { aspectRatio: result, aspectRatioError };
  }, []);

  // Build a fresh pending-file entry and fire off async dimension + thumbnail work.
  const buildAndEnqueuePendingFile = useCallback((file) => {
    pendingIdCounterRef.current += 1;
    const id = `pf_${Date.now()}_${pendingIdCounterRef.current}_${file.size}`;
    const mediaType = isVideoFile(file) ? 'video' : isImageFile(file) ? 'image' : 'other';
    const entry = {
      id,
      file,
      title: file.name.replace(/\.[^/.]+$/, ''),
      mediaType,
      thumbnailUrl: null,
      thumbnailRevocable: false,
      thumbnailLoading: true,
      thumbnailError: null,
      dimensions: null,
      aspectRatio: null,
      aspectRatioError: null,
      uploadStatus: 'validating',
      uploadError: null,
      contentId: null,
    };

    // Kick off dimension + aspect-ratio work
    getMediaDimensions(file)
      .then((dims) => {
        const sr = screenResolution;
        const { aspectRatio, aspectRatioError } = computeAspectRatioForDims(dims, sr);
        updatePendingFile(id, {
          dimensions: dims,
          aspectRatio,
          aspectRatioError,
          uploadStatus: 'pending',
        });
      })
      .catch(() => {
        updatePendingFile(id, {
          dimensions: null,
          aspectRatio: null,
          aspectRatioError: null, // can't validate without dims; allow through with a note
          uploadStatus: 'pending',
        });
      });

    // Kick off thumbnail generation
    generateThumbnail(file)
      .then((thumb) => {
        updatePendingFile(id, {
          thumbnailUrl: thumb.url,
          thumbnailRevocable: !!thumb.revocable,
          thumbnailLoading: false,
          thumbnailError: null,
        });
      })
      .catch((err) => {
        updatePendingFile(id, {
          thumbnailUrl: null,
          thumbnailRevocable: false,
          thumbnailLoading: false,
          thumbnailError: err?.message || 'thumbnail failed',
        });
      });

    return entry;
  }, [screenResolution, computeAspectRatioForDims, updatePendingFile]);

  // Re-validate every pending file when the target screen resolution changes.
  useEffect(() => {
    if (pendingFilesRef.current.length === 0) return;
    setPendingFiles((prev) => prev.map((pf) => {
      if (!pf.dimensions) return pf;
      const { aspectRatio, aspectRatioError } = computeAspectRatioForDims(pf.dimensions, screenResolution);
      return { ...pf, aspectRatio, aspectRatioError };
    }));
  }, [screenResolution, computeAspectRatioForDims]);

  // Revoke any object URLs we created for thumbnails when the pending list is cleared
  // or when the component unmounts.
  useEffect(() => {
    return () => {
      pendingFilesRef.current.forEach((pf) => {
        if (pf.thumbnailRevocable && pf.thumbnailUrl) {
          try { URL.revokeObjectURL(pf.thumbnailUrl); } catch { /* noop */ }
        }
      });
    };
  }, []);

  const hasAnyAspectMismatch = useMemo(
    () => pendingFiles.some((pf) => !!pf.aspectRatioError),
    [pendingFiles]
  );
  const pendingValidatingCount = useMemo(
    () => pendingFiles.filter((pf) => pf.uploadStatus === 'validating' || pf.thumbnailLoading).length,
    [pendingFiles]
  );
  const pendingDoneCount = useMemo(
    () => pendingFiles.filter((pf) => pf.uploadStatus === 'done').length,
    [pendingFiles]
  );
  const pendingFailedCount = useMemo(
    () => pendingFiles.filter((pf) => pf.uploadStatus === 'failed').length,
    [pendingFiles]
  );
  const pendingReadyCount = useMemo(
    () => pendingFiles.filter((pf) => pf.uploadStatus === 'pending').length,
    [pendingFiles]
  );

  const allLibraryItems = useMemo(
    () => Object.values(libraryBySection).flat(),
    [libraryBySection],
  );

  // Note: backend schedule currently stores only contentId, not section.
  // If duplicate contentIds exist across sections, schedule target can be ambiguous.
  const contentById = useMemo(() => {
    const map = {};
    allLibraryItems.forEach((item) => {
      if (item.contentId) {
        map[item.contentId] = item;
      }
    });
    return map;
  }, [allLibraryItems]);

  const scheduleByContentId = useMemo(() => {
    const map = {};
    screenSchedule.forEach((slot) => {
      map[slot.contentId] = slot;
    });
    return map;
  }, [screenSchedule]);

  // Library health metrics for the stat tiles. Computed across the full
  // library (not filtered), so counts are stable and represent the real
  // content state — filters just change which cards get rendered below.
  const itemStatus = useCallback((item) => {
    for (const k of [item?.contentId, item?.name, item?.fileName].filter(Boolean)) {
      const s = contentStatusByKey[k];
      if (s) return s;
    }
    return 'never_scheduled';
  }, [contentStatusByKey]);

  const mediaStats = useMemo(() => {
    const total = allLibraryItems.length;
    const now = Date.now();
    const WEEK_MS = 7 * 24 * 60 * 60 * 1000;
    let live = 0;
    let uploadedThisWeek = 0;
    for (const item of allLibraryItems) {
      if (itemStatus(item) === 'playing') live += 1;
      if (item.createdAt) {
        const t = Date.parse(item.createdAt);
        if (Number.isFinite(t) && now - t < WEEK_MS) uploadedThisWeek += 1;
      }
    }
    const unscheduled = Math.max(0, total - live);
    return { total, live, uploadedThisWeek, unscheduled };
  }, [allLibraryItems, itemStatus]);

  const refreshLibrary = useCallback(async (silent = false) => {
    if (!silent) {
      setIsLibraryLoading(true);
    }

    try {
      if (screensaverOnly) {
        try {
          const items = await listSectionContent(getActiveProjectId(), 'screensaver');
          const normalized = items.map((item) => normalizeContentItem(item, 'screensaver'));
          setLibraryBySection((prev) => {
            const next = { ...prev };
            SECTION_OPTIONS.forEach((o) => {
              if (!next[o.value]) next[o.value] = [];
            });
            next.screensaver = normalized;
            return next;
          });
        } catch (error) {
          console.error('Failed to load screensaver section:', error);
          setLibraryBySection((prev) => ({ ...prev, screensaver: [] }));
        }
      } else {
        const sectionResults = await Promise.all(
          SECTION_OPTIONS.map(async (option) => {
            try {
              const items = await listSectionContent(getActiveProjectId(), option.value);
              return [option.value, items.map((item) => normalizeContentItem(item, option.value))];
            } catch (error) {
              console.error(`Failed to load section ${option.value}:`, error);
              return [option.value, []];
            }
          }),
        );

        const nextLibrary = {};
        sectionResults.forEach(([sectionKey, items]) => {
          nextLibrary[sectionKey] = items;
        });

        setLibraryBySection(nextLibrary);
      }
      setLastRefreshedAt(new Date());
    } finally {
      if (!silent) {
        setIsLibraryLoading(false);
      }
    }
  }, [screensaverOnly]);

  // Initial load + interval polling so content list stays near real-time.
  useEffect(() => {
    refreshLibrary();

    const intervalId = setInterval(() => {
      refreshLibrary(true);
    }, REFRESH_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [refreshLibrary]);

  // Project-wide content status for the Library stat tiles. Indexed under every
  // identifier the content might be keyed by (contentId, contentNanoId, name)
  // so library-item lookups hit regardless of whether the media doc was normalised
  // with the nanoid or the filename.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await getContentStatus(getActiveProjectId());
        if (cancelled) return;
        const map = {};
        for (const c of (res?.content || [])) {
          for (const k of [c.contentId, c.contentNanoId, c.name].filter(Boolean)) {
            map[k] = c.status;
          }
        }
        setContentStatusByKey(map);
      } catch (err) {
        if (!cancelled) console.warn('content-status load failed:', err?.message);
      }
    };
    load();
    const id = setInterval(load, REFRESH_INTERVAL_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  const loadScreenSchedule = useCallback(async () => {
    if (!selectedTvId) {
      setScreenSchedule([]);
      setScheduleDrafts({});
      return;
    }

    setIsScheduleLoading(true);
    try {
      const schedule = await getScreenSchedule(getActiveProjectId(), selectedTvId);
      const normalizedSchedule = sortSchedule(
        schedule
          .filter((slot) => slot?.contentId && slot?.startTime && slot?.endTime)
          .map((slot) => {
            const row = {
              contentId: String(slot.contentId),
              startTime: String(slot.startTime),
              endTime: String(slot.endTime),
            };
            if (slot.url) row.url = String(slot.url);
            if (slot.loopEnabled === true) {
              row.loopEnabled = true;
              if (Array.isArray(slot.loopItems)) row.loopItems = slot.loopItems;
            }
            return row;
          }),
      );

      setScreenSchedule(normalizedSchedule);

      const nextDrafts = {};
      normalizedSchedule.forEach((slot) => {
        nextDrafts[slot.contentId] = {
          startTime: slot.startTime,
          endTime: slot.endTime,
        };
      });
      setScheduleDrafts(nextDrafts);
    } catch (error) {
      setActionMessage(error instanceof Error ? error.message : 'Failed to load schedule');
      setScreenSchedule([]);
      setScheduleDrafts({});
    } finally {
      setIsScheduleLoading(false);
    }
  }, [selectedTvId]);

  // Load schedule whenever TV changes.
  useEffect(() => {
    loadScreenSchedule();
    lastAutoPlayingContentIdRef.current = null;
  }, [loadScreenSchedule]);

  const handleFileChange = (event) => {
    const allFiles = Array.from(event.target.files || []);
    const needImage =
      screensaverOnly || section === 'screensaver' || contentType === 'screensaver';

    if (allFiles.length > 1) {
      // Multi-file selected — switch to card-grid flow
      const validFiles = needImage
        ? allFiles.filter(f => f.type.startsWith('image/'))
        : allFiles;
      const filtered = allFiles.length - validFiles.length;
      if (filtered > 0) {
        setErrorMessage(`${filtered} non-image file(s) were skipped because screensaver accepts images only.`);
      }
      // Revoke any existing thumbnails before replacing
      pendingFilesRef.current.forEach((pf) => {
        if (pf.thumbnailRevocable && pf.thumbnailUrl) {
          try { URL.revokeObjectURL(pf.thumbnailUrl); } catch { /* noop */ }
        }
      });
      setPendingFiles(validFiles.map((f) => buildAndEnqueuePendingFile(f)));
      setFile(null);
      setFilePreview(null);
      return;
    }

    const selectedFile = allFiles[0] || null;
    if (selectedFile) {
      if (needImage && !selectedFile.type.startsWith('image/')) {
        setErrorMessage('Screensaver accepts image files only.');
        event.target.value = '';
        return;
      }
    }
    setFile(selectedFile);
    setPendingFiles([]);
    // Auto-assign section from file type (images→gallery, videos→video, pdf→brochure)
    if (selectedFile && !screensaverOnly) {
      setSection(autoDetectSection(selectedFile.name));
    }
    handleFilePreview(selectedFile);
  };

  const handleFilePreview = (selectedFile) => {
    if (!selectedFile) {
      setFilePreview(null);
      return;
    }

    if (section === 'screensaver' || contentType === 'screensaver') {
      setContentType('screensaver');
    } else {
      setContentType(inferContentType(selectedFile, section));
    }

    if (!title.trim()) {
      setTitle(selectedFile.name.replace(/\.[^.]+$/, ''));
    }

    // Create preview URL
    if (selectedFile.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFilePreview({ type: 'image', url: reader.result });
      };
      reader.readAsDataURL(selectedFile);
    } else if (selectedFile.type.startsWith('video/')) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFilePreview({ type: 'video', url: reader.result });
      };
      reader.readAsDataURL(selectedFile);
    } else {
      setFilePreview({ type: 'file', name: selectedFile.name });
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFiles = Array.from(e.dataTransfer.files || []);
    if (droppedFiles.length === 0) return;

    const needImage =
      screensaverOnly || section === 'screensaver' || contentType === 'screensaver';

    if (droppedFiles.length === 1) {
      // Single file — use existing flow
      const droppedFile = droppedFiles[0];
      if (needImage && !droppedFile.type.startsWith('image/')) {
        setErrorMessage('Screensaver accepts image files only.');
        return;
      }
      setFile(droppedFile);
      setPendingFiles([]);
      handleFilePreview(droppedFile);
    } else {
      // Multi-file — populate pendingFiles for batch upload
      const validFiles = needImage
        ? droppedFiles.filter(f => f.type.startsWith('image/'))
        : droppedFiles;
      const filtered = droppedFiles.length - validFiles.length;
      if (filtered > 0) {
        setErrorMessage(`${filtered} non-image file(s) were skipped because screensaver accepts images only.`);
      }
      pendingFilesRef.current.forEach((pf) => {
        if (pf.thumbnailRevocable && pf.thumbnailUrl) {
          try { URL.revokeObjectURL(pf.thumbnailUrl); } catch { /* noop */ }
        }
      });
      setPendingFiles(validFiles.map((f) => buildAndEnqueuePendingFile(f)));
      setFile(null);
      setFilePreview(null);
    }
  };

  const handleSectionChange = (event) => {
    const nextSection = event.target.value;
    setSection(nextSection);

    if (nextSection === 'screensaver') {
      setContentType('screensaver');
    } else if (file) {
      setContentType(inferContentType(file, nextSection));
    }
  };

  const handleBatchUpload = async () => {
    // Hard-block on any aspect ratio mismatch
    if (pendingFiles.some((pf) => !!pf.aspectRatioError)) {
      setErrorMessage('Fix aspect ratio mismatches before uploading.');
      return;
    }

    // Duplicate-title check (skip if user already confirmed)
    if (duplicateUploadConflict !== 'confirmed') {
      const toCheck = pendingFiles.filter(
        (pf) => pf.uploadStatus === 'pending' || pf.uploadStatus === 'failed'
      );
      const batchConflicts = toCheck.reduce((acc, pf) => {
        const pfTitle = (pf.title || pf.file?.name || '').trim().toLowerCase();
        if (!pfTitle) return acc;
        const existing = allLibraryItems.find(
          (item) => (item.title || item.name || '').toLowerCase() === pfTitle,
        );
        if (existing) acc.push({ file: pf, existing });
        return acc;
      }, []);
      if (batchConflicts.length > 0) {
        setDuplicateUploadConflict({ batchConflicts });
        return;
      }
    }
    setDuplicateUploadConflict(null);

    // Upload only pending/failed entries — leave already-done ones alone so
    // the user can retry partial failures without re-uploading succeeded files.
    const toUpload = pendingFiles.filter(
      (pf) => pf.uploadStatus === 'pending' || pf.uploadStatus === 'failed'
    );
    if (toUpload.length === 0) {
      setErrorMessage('No files to upload.');
      return;
    }

    const uploadSection =
      screensaverOnly || contentType === 'screensaver' || section === 'screensaver'
        ? 'screensaver'
        : section;

    // Mark retried files back to 'pending' so the UI reflects the re-queue
    setPendingFiles((prev) => prev.map((pf) => (
      pf.uploadStatus === 'failed' ? { ...pf, uploadStatus: 'pending', uploadError: null } : pf
    )));

    setIsUploading(true);
    setErrorMessage('');
    setSuccessMessage('');
    setBatchProgress({ completed: 0, total: toUpload.length });

    // Shared extra fields (expiry/permanent/category) applied to every file
    // in the batch. Category is form-level: marketing user picks once, applies
    // to all N files in this upload session.
    const sharedExtra = {
      is_permanent: isPermanent,
      ...(validFrom ? { valid_from: validFrom } : {}),
      ...(validUntil && !isPermanent ? { valid_until: validUntil } : {}),
      ...(category ? { category } : {}),
    };

    try {
      const result = await uploadBatch({
        files: toUpload.map((pf) => ({
          id: pf.id,
          file: pf.file,
          title: pf.title,
          dims: pf.dimensions || undefined,
          extra: sharedExtra,
        })),
        projectId: getActiveProjectId(),
        section: uploadSection,
        targetScreenId: selectedTvId || undefined,
        callbacks: {
          onFileStatusChange: (entry, _idx, status, meta) => {
            if (status === 'uploading') {
              updatePendingFile(entry.id, { uploadStatus: 'uploading', uploadError: null });
            } else if (status === 'done') {
              updatePendingFile(entry.id, {
                uploadStatus: 'done',
                uploadError: null,
                contentId: meta?.contentId || null,
              });
            } else if (status === 'failed') {
              updatePendingFile(entry.id, {
                uploadStatus: 'failed',
                uploadError: meta?.error || 'Upload failed',
              });
            }
          },
          onProgress: (completed, total) => {
            setBatchProgress({ completed, total });
            setStatusMessage(`Uploading ${completed}/${total} files...`);
          },
        },
      });

      setIsUploading(false);
      setBatchProgress(null);
      setStatusMessage('');

      if (result.failed === 0) {
        // Full success — close modal and reset
        const successfulItems = pendingFilesRef.current
          .filter((pf) => pf.uploadStatus === 'done' && pf.contentId)
          .map((pf) => ({
            id: pf.contentId,
            contentId: pf.contentId,
            title: pf.title || pf.file?.name || 'Uploaded content',
            url: '',
            type: pf.file?.type?.startsWith('video/') ? 'video' : 'image',
            ...(pf.dimensions ? { width: pf.dimensions.width, height: pf.dimensions.height } : {}),
          }));
        // Revoke thumbnail URLs before wiping pendingFiles
        pendingFilesRef.current.forEach((pf) => {
          if (pf.thumbnailRevocable && pf.thumbnailUrl) {
            try { URL.revokeObjectURL(pf.thumbnailUrl); } catch { /* noop */ }
          }
        });
        setIsUploadModalOpen(false);
        setPendingFiles([]);
        setFile(null);
        setFilePreview(null);
        setTitle('');
        setCategory(null);
        setSuccessMessage(`Uploaded ${result.succeeded} file(s).`);
        // Open schedule modal for the first uploaded item — user can schedule
        // others from the library card. Skip for screensaver section.
        if (uploadSection !== 'screensaver' && successfulItems.length > 0) {
          setQuickScheduleItem(successfulItems[0]);
        }
      } else {
        // Partial failure — keep modal open so the user can see per-card errors + retry
        setErrorMessage(`Uploaded ${result.succeeded} of ${result.succeeded + result.failed}. ${result.failed} failed — see cards below.`);
      }
      refreshLibrary();
    } catch (err) {
      setIsUploading(false);
      setBatchProgress(null);
      setErrorMessage(`Batch upload failed: ${err.message}`);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setErrorMessage('Please select a file to upload.');
      return;
    }

    if (!title.trim()) {
      setErrorMessage('Title is required.');
      return;
    }

    if (!category) {
      setErrorMessage('Category is required.');
      return;
    }

    if (aspectRatioError) {
      setErrorMessage(aspectRatioError);
      return;
    }

    // Duplicate-title check (skip if user already confirmed)
    if (duplicateUploadConflict !== 'confirmed') {
      const dupTitle = title.trim().toLowerCase();
      const existing = allLibraryItems.find(
        (item) => (item.title || item.name || '').toLowerCase() === dupTitle,
      );
      if (existing) {
        setDuplicateUploadConflict(existing);
        return;
      }
    }
    setDuplicateUploadConflict(null);

    // If multi-file mode, use batch upload
    if (pendingFiles.length > 0) {
      return handleBatchUpload();
    }

    const uploadSection =
      screensaverOnly || contentType === 'screensaver' || section === 'screensaver'
        ? 'screensaver'
        : section;
    const uploadType = contentType === 'screensaver' ? 'image' : contentType;

    if (uploadSection === 'screensaver' && file && !file.type.startsWith('image/')) {
      setErrorMessage('Screensaver accepts image files only.');
      return;
    }

    setIsUploading(true);
    setStatusMessage('');
    setErrorMessage('');
    setSuccessMessage('');

    try {
      setStatusMessage('Step 1/3: Requesting upload policy...');
      const policy = await getUploadPolicy(file.name, getActiveProjectId(), uploadSection);

      setStatusMessage('Step 2/3: Uploading to S3...');
      await uploadFileToS3(file, policy);

      setStatusMessage('Step 3/3: Saving metadata...');
      await createContent({
        contentId: policy.contentId,
        filePath: policy.filePath,
        projectId: getActiveProjectId(),
        section: uploadSection,
        fileName: policy.fileName || file.name,
        type: uploadType,
        title: title.trim(),
        description: '',
        tags: [],
        category,
        ...(fileDimensions ? { width: fileDimensions.width, height: fileDimensions.height } : {}),
        ...(fileDimensions && fileDimensions.duration ? { duration: fileDimensions.duration } : {}),
        ...(selectedTvId ? { target_screen_id: selectedTvId } : {}),
        is_permanent: isPermanent,
        ...(validFrom ? { valid_from: validFrom } : {}),
        ...(validUntil && !isPermanent ? { valid_until: validUntil } : {}),
      });
      // IMPORTANT: the manifest (which the TV app resolves schedules
      // against) keys items by SumadhuraCms.name, NOT by content_id.
      // Note: we no longer derive savedContentName here. It was used by the
      // (now-removed) schedule-after-upload path to pass the manifest id into
      // createCmsSchedule. Scheduling is now a separate flow from the library
      // card which already handles manifest-id resolution.

      const uploaderEmail = String(localStorage.getItem('userEmail') || '').trim();
      const emailLooksValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(uploaderEmail);
      if (emailLooksValid) {
        postContentUploadAnalytics({
          email: uploaderEmail,
          projectId: getActiveProjectId(),
          section: uploadSection,
          contentId: policy.contentId,
          contentType: uploadType,
          fileName: policy.fileName || file.name,
          title: title.trim(),
          fileSize: Math.max(0, Math.floor(Number(file.size) || 0)),
          mimeType: file.type || 'application/octet-stream',
        }).catch((err) => {
          console.error('Content upload analytics failed:', err);
        });
      } else {
        console.warn(
          'Content upload analytics skipped: set localStorage userEmail to a valid email.',
        );
      }

      // Upload successful — close upload modal and pre-open the schedule
      // modal with this item. User can schedule immediately or click Cancel
      // to skip and just have it in the library.
      const uploadedItem = {
        id: policy.contentId,
        contentId: policy.contentId,
        title: title.trim(),
        url: '',
        type: uploadType,
        ...(fileDimensions ? { width: fileDimensions.width, height: fileDimensions.height } : {}),
      };

      setIsUploadModalOpen(false);
      setIsUploading(false);

      // Reset all form fields
      setTitle('');
      setCategory(null);
      setFile(null);
      setFilePreview(null);
      setErrorMessage('');
      setSuccessMessage('');
      setStatusMessage('');
      setIsDragging(false);
      setFileInputKey((current) => current + 1);
      setActiveLibrarySection(uploadSection);

      // Refresh library then open the schedule modal. We skip the schedule
      // prompt for screensaver uploads since those play automatically.
      refreshLibrary(true)
        .catch((err) => { console.error('Background library refresh failed:', err); })
        .finally(() => {
          if (uploadSection !== 'screensaver') {
            setQuickScheduleItem(uploadedItem);
          }
        });
    } catch (error) {
      setStatusMessage('');
      setErrorMessage(error instanceof Error ? error.message : 'Upload failed');
      setIsUploading(false);
    }
  };

  /**
   * Aspect-ratio compatibility classifier for the quick-schedule modal.
   * For each screen, returns one of:
   *   - 'compatible'   — screen AR matches content AR (within Phase 2 tolerance)
   *   - 'incompatible' — known mismatch, blocked from selection
   *   - 'unknown'      — content has no dims OR screen has no nativeResolution,
   *                       can't validate, treated as compatible (don't block)
   *
   * Mirrors the upload-flow validation in handleUpload so users get a
   * consistent guardrail whether they're uploading new content or
   * scheduling existing library content. Same `checkAspectRatioMatch`
   * helper as the upload modal.
   */
  const getQuickScheduleScreenCompat = (screen) => {
    const cw = Number(quickScheduleItem?.width) || 0;
    const ch = Number(quickScheduleItem?.height) || 0;
    // Resolve through profile first — a portrait kiosk with EDID=1920×1080
    // must validate content against 1080×1920 (profile.content), not the
    // EDID reading, or we'd block every portrait piece on a portrait kiosk.
    const dims = resolveScreenDims(screen);
    const sw = dims?.width || 0;
    const sh = dims?.height || 0;
    if (!cw || !ch || !sw || !sh) return { status: 'unknown' };
    const result = checkAspectRatioMatch(cw, ch, sw, sh);
    if (result.valid) return { status: 'compatible', ...result };
    return { status: 'incompatible', ...result };
  };

  const handleQuickScheduleSubmit = async () => {
    if (!quickScheduleItem || quickScheduleScreenIds.length === 0 || !quickScheduleStartTime || !quickScheduleEndTime) return;
    setQuickScheduleSaving(true);
    setQuickScheduleMsg('');
    // Defensive filter: drop any incompatible screens that somehow got into
    // the selection (shouldn't happen since the checkboxes are disabled,
    // but stale state from a screen-add race could let one slip through).
    const validScreenIds = quickScheduleScreenIds.filter((sid) => {
      const screen = tvs.find((tv) => (tv.deviceId || tv.tvId) === sid);
      return getQuickScheduleScreenCompat(screen).status !== 'incompatible';
    });
    if (validScreenIds.length === 0) {
      setQuickScheduleSaving(false);
      setQuickScheduleMsg('Failed: all selected screens have incompatible aspect ratios.');
      return;
    }
    const skippedCount = quickScheduleScreenIds.length - validScreenIds.length;
    try {
      let ok = 0;
      let fail = 0;
      for (const screenId of validScreenIds) {
        try {
          const payload = {
            projectId: getActiveProjectId(),
            screenId,
            // IMPORTANT: quickScheduleItem comes from the manifest, where
            // `id` IS the SumadhuraCms.name (the field the TV app's
            // schedule resolver matches against). Prefer `id` first — the
            // legacy `contentId` / `title` fallbacks are unreliable since
            // `title` often contains spaces while the manifest id has
            // underscores, and `contentId` (the nanoid) won't match the
            // manifest at all. See backend getProjectManifest line ~732.
            contentId: quickScheduleItem.id || quickScheduleItem.contentId || quickScheduleItem.title,
            startTime: quickScheduleStartTime,
            endTime: quickScheduleEndTime,
          };
          if (quickScheduleEndDate) {
            payload.startDate = quickScheduleStartDate;
            payload.endDate = quickScheduleEndDate;
          } else {
            payload.date = quickScheduleStartDate;
          }
          await createScheduleFromLegacyShape({
            ...payload,
            name: quickScheduleItem?.title || quickScheduleItem?.name || payload.contentId,
          });
          ok++;
        } catch { fail++; }
      }
      const skipNote = skippedCount > 0 ? ` (${skippedCount} skipped — wrong AR)` : '';
      setQuickScheduleMsg(fail === 0 ? `Scheduled on ${ok} screen(s)${skipNote}` : `${ok} scheduled, ${fail} failed${skipNote}`);
      setTimeout(() => {
        setQuickScheduleItem(null);
        setQuickScheduleMsg('');
        setQuickScheduleScreenIds([]);
        setQuickScheduleStartTime('');
        setQuickScheduleEndTime('');
        setQuickScheduleEndDate('');
      }, 1200);
    } catch (err) {
      setQuickScheduleMsg(`Failed: ${err.message}`);
    } finally {
      setQuickScheduleSaving(false);
    }
  };

  const handlePlay = (item) => {
    if (!selectedTvId) {
      setActionMessage('Select a TV first to play content.');
      return;
    }

    if (!item.url) {
      setActionMessage('This content does not have a playable URL.');
      return;
    }

    try {
      const isVideo = item.type === 'video' || item.section === 'video';
      tvSocket.playContent(selectedTvId, {
        type: isVideo ? 'video' : 'url',
        url: item.url,
      });
      setActionMessage(`Play command sent to ${selectedTvId} for "${item.title}".`);
    } catch (error) {
      setActionMessage(error instanceof Error ? error.message : 'Failed to send play command.');
    }
  };

  const handleDraftChange = (contentId, fieldName, value) => {
    setScheduleDrafts((previous) => ({
      ...previous,
      [contentId]: {
        ...(previous[contentId] || {}),
        [fieldName]: value,
      },
    }));
  };

  const handleSaveSchedule = async (item) => {
    if (!selectedTvId) {
      setActionMessage('Select a TV first to save schedule.');
      return;
    }

    const draft = scheduleDrafts[item.contentId] || {};
    const startTime = draft.startTime || '';
    const endTime = draft.endTime || '';

    if (!startTime || !endTime) {
      setActionMessage('Start and end time are required.');
      return;
    }

    if (startTime === endTime) {
      setActionMessage('Start time and end time cannot be equal.');
      return;
    }

    const filteredSchedule = screenSchedule.filter((slot) => slot.contentId !== item.contentId);
    const nextSchedule = sortSchedule([
      ...filteredSchedule,
      {
        contentId: item.contentId,
        url: item.url,
        startTime,
        endTime,
      },
    ]);

    setIsScheduleSaving(true);
    try {
      console.log('[Schedule] Saving screen schedule', {
        projectId: getActiveProjectId(),
        screenId: selectedTvId,
        item: item.contentId,
        startTime,
        endTime,
      });
      await upsertScreenSchedule(getActiveProjectId(), selectedTvId, nextSchedule);
      setScreenSchedule(nextSchedule);
      setActionMessage(
        `Schedule saved for "${item.title}" on ${selectedTvId}: ${startTime} - ${endTime}`,
      );
    } catch (error) {
      setActionMessage(error instanceof Error ? error.message : 'Failed to save schedule');
    } finally {
      setIsScheduleSaving(false);
    }
  };

  const handleRemoveSchedule = async (item) => {
    if (!selectedTvId) {
      setActionMessage('Select a TV first to remove schedule.');
      return;
    }

    const nextSchedule = sortSchedule(
      screenSchedule.filter((slot) => slot.contentId !== item.contentId),
    );

    setIsScheduleSaving(true);
    try {
      console.log('[Schedule] Removing slot', {
        projectId: getActiveProjectId(),
        screenId: selectedTvId,
        item: item.contentId,
      });
      await upsertScreenSchedule(getActiveProjectId(), selectedTvId, nextSchedule);
      setScreenSchedule(nextSchedule);
      setScheduleDrafts((previous) => ({
        ...previous,
        [item.contentId]: {
          startTime: '',
          endTime: '',
        },
      }));
      setActionMessage(`Schedule removed for "${item.title}" on ${selectedTvId}.`);
    } catch (error) {
      setActionMessage(error instanceof Error ? error.message : 'Failed to remove schedule');
    } finally {
      setIsScheduleSaving(false);
    }
  };

  // Auto scheduler:
  // Executes start/stop based on saved slots while this dashboard is open.
  useEffect(() => {
    if (!selectedTvId || screenSchedule.length === 0) {
      return undefined;
    }

    const runAutoScheduler = () => {
      try {
        const now = new Date();
        const nowMinutes = (now.getHours() * 60) + now.getMinutes();

        const activeSlot = screenSchedule.find((slot) => (
          isInTimeRange(nowMinutes, slot.startTime, slot.endTime)
        ));

        const nextContentId = activeSlot?.contentId || null;
        const previousContentId = lastAutoPlayingContentIdRef.current;

        if (nextContentId === previousContentId) {
          return;
        }

        if (!nextContentId && previousContentId) {
          console.log('[Scheduler] Auto stop triggered', {
            screenId: selectedTvId,
            previousContentId,
          });
          tvSocket.stopContent(selectedTvId);
          lastAutoPlayingContentIdRef.current = null;
          setActionMessage(
            `Auto stop executed on ${selectedTvId} because schedule end time was reached.`,
          );
          return;
        }

        if (nextContentId) {
          const item = contentById[nextContentId];
          if (!item?.url) {
            setActionMessage(
              `Scheduled content "${nextContentId}" has no URL. Unable to auto-play.`,
            );
            return;
          }

          const isVideo = item.type === 'video' || item.section === 'video';
          console.log('[Scheduler] Auto start triggered', {
            screenId: selectedTvId,
            contentId: nextContentId,
            startTime: activeSlot.startTime,
            endTime: activeSlot.endTime,
          });
          tvSocket.playContent(selectedTvId, {
            type: isVideo ? 'video' : 'url',
            url: item.url,
          });

          lastAutoPlayingContentIdRef.current = nextContentId;
          setActionMessage(
            `Auto start executed on ${selectedTvId} for "${item.title}" (${activeSlot.startTime}-${activeSlot.endTime}).`,
          );
        }
      } catch (error) {
        setActionMessage(error instanceof Error ? error.message : 'Auto scheduler failed');
      }
    };

    runAutoScheduler();
    const intervalId = setInterval(runAutoScheduler, AUTO_SCHEDULER_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [selectedTvId, screenSchedule, contentById]);

  const activeSectionItems = libraryBySection[activeLibrarySection] || [];
  const lastRefreshedLabel = useMemo(() => {
    if (!lastRefreshedAt) return 'Never';
    return new Intl.DateTimeFormat('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    }).format(lastRefreshedAt);
  }, [lastRefreshedAt]);

  // Filter and search media items (Screensaver sidebar: only that section)
  const filteredItems = useMemo(() => {
    let items = screensaverOnly
      ? (libraryBySection.screensaver || [])
      : allLibraryItems;

    // Filter by content type
    if (contentFilter === 'images') {
      items = items.filter((item) => isImageItem(item));
    } else if (contentFilter === 'videos') {
      items = items.filter((item) => isVideoItem(item));
    } else if (contentFilter === 'brochures') {
      items = items.filter((item) => item.section === 'brochure');
    }

    // Filter by schedule status
    if (scheduleFilter === 'live') {
      items = items.filter((item) => itemStatus(item) === 'playing');
    } else if (scheduleFilter === 'unscheduled') {
      items = items.filter((item) => itemStatus(item) !== 'playing');
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      items = items.filter((item) =>
        item.title?.toLowerCase().includes(query) ||
        item.contentId?.toLowerCase().includes(query) ||
        item.fileName?.toLowerCase().includes(query)
      );
    }

    return items;
  }, [allLibraryItems, libraryBySection.screensaver, screensaverOnly, contentFilter, scheduleFilter, searchQuery, itemStatus]);

  // Per-type counts for the segmented pills so the user sees how many items
  // they'd get if they flipped filters. Computed from the pre-filter set so
  // counts don't change as filters are applied.
  const typeCounts = useMemo(() => {
    const base = screensaverOnly
      ? (libraryBySection.screensaver || [])
      : allLibraryItems;
    let videos = 0;
    let images = 0;
    for (const item of base) {
      if (isVideoItem(item)) videos += 1;
      else if (isImageItem(item)) images += 1;
    }
    return { all: base.length, videos, images };
  }, [allLibraryItems, libraryBySection.screensaver, screensaverOnly]);

  // Group filteredItems by section so we can render section headers between
  // them. A "Currently scheduled" pseudo-section gets hoisted to the top if
  // any filtered items have active schedules — the marketing-head's first
  // question is "what's live right now?", so we answer it visually first.
  // Returns an array of { key, label, items, tone? } in display order.
  const filteredSections = useMemo(() => {
    if (screensaverOnly) return null; // screensaver mode keeps its flat layout
    // Pretty labels keyed by section id from SECTION_OPTIONS. Anything not
    // in this map just gets title-cased.
    const SECTION_LABELS = {
      brochure: 'Brochures',
      specifications: 'Specifications',
      video: 'Videos',
      floorplan: 'Floor plans',
      masterplan: 'Master plans',
      gallery: 'Gallery',
      location: 'Location',
      screensaver: 'Screensaver',
    };
    const SECTION_ORDER = [
      'gallery',
      'video',
      'brochure',
      'floorplan',
      'masterplan',
      'specifications',
      'location',
      'screensaver',
    ];
    const groups = new Map();
    const live = [];
    for (const item of filteredItems) {
      const sectionKey = item.section || 'other';
      if (!groups.has(sectionKey)) groups.set(sectionKey, []);
      groups.get(sectionKey).push(item);
      if (item.contentId && scheduleByContentId[item.contentId]) {
        live.push(item);
      }
    }
    const result = [];
    if (live.length > 0) {
      result.push({
        key: '__live__',
        label: 'Currently scheduled',
        items: live,
        tone: 'live',
      });
    }
    for (const key of SECTION_ORDER) {
      if (groups.has(key) && groups.get(key).length > 0) {
        result.push({
          key,
          label: SECTION_LABELS[key] || key,
          items: groups.get(key),
        });
        groups.delete(key);
      }
    }
    // Remaining unknown sections, alphabetized
    const remaining = Array.from(groups.entries()).sort(([a], [b]) => a.localeCompare(b));
    for (const [key, items] of remaining) {
      result.push({
        key,
        label: SECTION_LABELS[key] || (key.charAt(0).toUpperCase() + key.slice(1)),
        items,
        tone: 'muted',
      });
    }
    return result;
  }, [filteredItems, scheduleByContentId, screensaverOnly]);

  /** Full catalog for bulk actions — must not use `filteredItems` or search/type filters hide selected rows and download/share/playlist break. */
  const libraryItemsForBulkActions = useMemo(() => {
    if (screensaverOnly) return libraryBySection.screensaver || [];
    return allLibraryItems;
  }, [screensaverOnly, libraryBySection.screensaver, allLibraryItems]);

  const resolveSelectedLibraryItems = () => {
    const seen = new Set();
    const out = [];
    for (const item of libraryItemsForBulkActions) {
      if (!selectedItems.has(item.id)) continue;
      if (seen.has(item.id)) continue;
      seen.add(item.id);
      out.push(item);
    }
    return out;
  };

  const handleToggleSelection = (itemId) => {
    setSelectedItems((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) {
        next.delete(itemId);
      } else {
        next.add(itemId);
      }
      return next;
    });
  };

  const handleSelectAll = () => {
    if (selectedItems.size === filteredItems.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(filteredItems.map((item) => item.id)));
    }
  };

  const handleShare = async () => {
    if (selectedItems.size === 0) return;

    const selectedItemsData = resolveSelectedLibraryItems();
    
    if (selectedItemsData.length === 0) {
      setActionMessage('No items selected to share.');
      return;
    }

    // Create shareable content
    const shareText = selectedItemsData
      .map((item) => `${item.title || item.name}: ${item.url || 'No URL available'}`)
      .join('\n');

    // Try Web Share API first (mobile/desktop native share)
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Sharing ${selectedItemsData.length} item(s)`,
          text: shareText,
        });
        setActionMessage(`✅ Shared ${selectedItemsData.length} item(s)`);
        setTimeout(() => setActionMessage(''), 3000);
        return;
      } catch (err) {
        // User cancelled or share failed, fall through to clipboard
        if (err.name !== 'AbortError') {
          console.error('Share failed:', err);
        }
      }
    }

    // Fallback: Copy to clipboard
    try {
      await navigator.clipboard.writeText(shareText);
      setActionMessage(`✅ Copied ${selectedItemsData.length} item(s) to clipboard`);
      setTimeout(() => setActionMessage(''), 3000);
    } catch (err) {
      console.error('Clipboard copy failed:', err);
      setActionMessage('❌ Failed to share. Please try again.');
      setTimeout(() => setActionMessage(''), 3000);
    }
  };

  const handleDownload = async () => {
    if (selectedItems.size === 0) return;

    const selectedItemsData = resolveSelectedLibraryItems();

    if (selectedItemsData.length === 0) {
      setActionMessage('No items selected to download.');
      setTimeout(() => setActionMessage(''), 3000);
      return;
    }

    setActionMessage(`Downloading ${selectedItemsData.length} item(s)...`);

    let okCount = 0;
    let blobCount = 0;
    let skipNoUrl = 0;

    for (const item of selectedItemsData) {
      if (!item.url) {
        skipNoUrl += 1;
        console.warn(`Item ${item.id} has no URL, skipping`);
        continue;
      }
      const name = buildDownloadFileName(item);
      const { ok, usedBlob } = await downloadFromUrl(item.url, name);
      if (ok) {
        okCount += 1;
        if (usedBlob) blobCount += 1;
      }
      await new Promise((r) => setTimeout(r, 250));
    }

    if (okCount === 0) {
      setActionMessage(
        skipNoUrl > 0
          ? '❌ No downloadable URLs for the selection.'
          : '❌ Download failed. Try again or use Save as in the new tab.',
      );
    } else if (okCount < selectedItemsData.length) {
      setActionMessage(
        `✅ ${okCount} of ${selectedItemsData.length} item(s) started${
          skipNoUrl ? ` (${skipNoUrl} missing URL)` : ''
        }.${blobCount < okCount ? ' Some opened in a new tab — use Save as if needed.' : ''}`,
      );
    } else if (blobCount === okCount) {
      setActionMessage(`✅ Downloaded ${okCount} item(s).`);
    } else {
      setActionMessage(
        `✅ Opened ${okCount} item(s) in a new tab (CDN). Right-click or use Save as to download.`,
      );
    }
    setTimeout(() => setActionMessage(''), 5000);
  };

  const handleOpenAddToPlaylist = () => setShowAddToPlaylistModal(true);
  const handleCloseAddToPlaylistModal = () => {
    if (!addToPlaylistSaving) {
      setShowAddToPlaylistModal(false);
      setPlaylistName('');
      setPlaylistDescription('');
      setAddToPlaylistError(null);
      setAddToPlaylistSuccess(null);
    }
  };

  const handleAddToPlaylistSubmit = async (e) => {
    e.preventDefault();
    const name = playlistName?.trim();
    if (!name) {
      setAddToPlaylistError('Name is required.');
      return;
    }
    const selectedItemsData = resolveSelectedLibraryItems();
    // Send name or title of each selected card in the payload (not content_id).
    const contentIds = selectedItemsData
      .map((item) => (item.title || item.name || item.contentId || '').trim())
      .filter(Boolean);
    if (contentIds.length === 0) {
      setAddToPlaylistError('Selected items have no name or title.');
      return;
    }
    try {
      setAddToPlaylistSaving(true);
      setAddToPlaylistError(null);
      setAddToPlaylistSuccess(null);
      await createPlaylist({
        projectId: getActiveProjectId(),
        name,
        description: playlistDescription?.trim() || '',
        contentIds,
      });
      setAddToPlaylistSuccess('Playlist created.');
      setActionMessage(`Playlist "${name}" created with ${contentIds.length} item(s).`);
      setTimeout(() => setActionMessage(''), 3000);
      setTimeout(() => {
        handleCloseAddToPlaylistModal();
        setSelectedItems(new Set());
      }, 800);
    } catch (err) {
      setAddToPlaylistError(err?.message || 'Failed to create playlist.');
    } finally {
      setAddToPlaylistSaving(false);
    }
  };

  const handleAddToUploadForm = (item) => {
    setSection(item.section || 'brochure');
    setTitle(item.title || '');
    setCategory(item.category || null);
    setContentType(item.type || 'image');
    setFile(null);
    setFilePreview(null);
    setIsUploadModalOpen(true);
    setContextMenuOpen(null);
  };

  const handleCloseModal = () => {
    // Revoke any in-flight thumbnail object URLs
    pendingFilesRef.current.forEach((pf) => {
      if (pf.thumbnailRevocable && pf.thumbnailUrl) {
        try { URL.revokeObjectURL(pf.thumbnailUrl); } catch { /* noop */ }
      }
    });
    setIsUploadModalOpen(false);
    setFile(null);
    setFilePreview(null);
    setPendingFiles([]);
    setBatchProgress(null);
    setErrorMessage('');
    setSuccessMessage('');
    setStatusMessage('');
    setIsDragging(false);
    setCategory(null);
  };

  const handleContextMenu = (e, itemId) => {
    e.preventDefault();
    e.stopPropagation(); 
    setContextMenuOpen(contextMenuOpen === itemId ? null : itemId);
  };

  useEffect(() => {
    const handleClickOutside = () => {
      setContextMenuOpen(null);
    };
    if (contextMenuOpen) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [contextMenuOpen]);

  return (
    <div style={newContainerStyle}>
      {/* Main Content */}
      <main style={mainContentStyle} className="media">
        {/* ── Stats row — clickable filter cards ─────────────────── */}
        {!screensaverOnly && (
          <div className="media__stats">
            <button
              type="button"
              className={`media-stat media-stat--brand${scheduleFilter === 'all' && contentFilter === 'images' ? ' media-stat--active' : ''}`}
              onClick={() => { setContentFilter('images'); setScheduleFilter('all'); }}
            >
              <div className="media-stat__label-row">
                <span className="media-stat__icon"><Tv2 size={15} /></span>
                Total assets
              </div>
              <div className="media-stat__value">{mediaStats.total}</div>
              <div className="media-stat__caption">Across all types</div>
            </button>
            <button
              type="button"
              className={`media-stat media-stat--good${scheduleFilter === 'live' ? ' media-stat--active' : ''}`}
              onClick={() => setScheduleFilter(scheduleFilter === 'live' ? 'all' : 'live')}
            >
              <div className="media-stat__label-row">
                <span className="media-stat__icon"><Radio size={15} /></span>
                Live
              </div>
              <div className="media-stat__value">{mediaStats.live}</div>
              <div className="media-stat__caption">Playing on a screen now</div>
            </button>
            <button
              type="button"
              className={`media-stat media-stat--warn${scheduleFilter === 'unscheduled' ? ' media-stat--active' : ''}`}
              onClick={() => setScheduleFilter(scheduleFilter === 'unscheduled' ? 'all' : 'unscheduled')}
            >
              <div className="media-stat__label-row">
                <span className="media-stat__icon"><AlertCircle size={15} /></span>
                Unscheduled
              </div>
              <div className="media-stat__value">{mediaStats.unscheduled}</div>
              <div className="media-stat__caption">Not on any screen</div>
            </button>
          </div>
        )}

        {/* ── Toolbar (legacy — v2 shell ships its own). Hidden when v2 is on
             for non-screensaver mode to avoid duplicate controls. ─────────── */}
        {(!isMediaV2Enabled() || screensaverOnly) && (
          <div className="media__toolbar">
            <div className="media__toolbar-left">
              {!screensaverOnly && (
                <div className="media__section-filter">
                  {FILTER_SECTIONS.map((o) => (
                    <button
                      key={o.value}
                      type="button"
                      className={`media__section-btn${contentFilter === o.value ? ' media__section-btn--active' : ''}`}
                      onClick={() => setContentFilter(o.value)}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
              )}
              <div className="media__search">
                <span className="media__search-icon"><Search size={16} /></span>
                <input
                  type="text"
                  className="media__search-input"
                  placeholder="Search assets by name, id, or file…"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                {searchQuery && (
                  <button
                    type="button"
                    className="media__search-clear"
                    onClick={() => setSearchQuery('')}
                    aria-label="Clear search"
                  >
                    <X size={14} />
                  </button>
                )}
              </div>
            </div>
            <div className="media__toolbar-right">
              {(scheduleFilter !== 'all') && (
                <button
                  type="button"
                  className="media__search-clear"
                  onClick={() => { setScheduleFilter('all'); }}
                  aria-label="Clear filters"
                  title="Clear all filters"
                >
                  <X size={14} />
                </button>
              )}
              <span className="media__result-count">
                <strong>{filteredItems.length}</strong>
                {filteredItems.length === 1 ? ' asset' : ' assets'}
                {searchQuery && ' found'}
              </span>
              <Button
                variant="ghost"
                size="sm"
                iconOnly
                aria-label="Refresh"
                loading={isLibraryLoading}
                onClick={() => refreshLibrary()}
              >
                <RefreshCw size={15} />
              </Button>
              <Button
                variant="primary"
                size="sm"
                leftIcon={<Plus size={14} />}
                onClick={() => {
                  setFile(null);
                  setFilePreview(null);
                  if (screensaverOnly) {
                    setSection('screensaver');
                    setContentType('screensaver');
                  }
                  setIsUploadModalOpen(true);
                }}
              >
                Upload
              </Button>
            </div>
          </div>
        )}

        {/* ── Media cards: v2 shell (tabs + folders + DnD) or legacy flat grid.
             Screensaver keeps the flat legacy layout (no folders/archive). ── */}
        {isMediaV2Enabled() && !screensaverOnly ? (
          <MediaV2Shell
            allLibraryItems={filteredItems}
            scheduleByContentId={scheduleByContentId}
            renderCard={(item, opts) => renderMediaCard(item, { ...opts, onPreview: setPreviewItem })}
            onSchedule={(item) => {
              setQuickScheduleItem(item);
              setQuickScheduleStartDate(new Date().toISOString().slice(0, 10));
              setQuickScheduleEndDate('');
              setQuickScheduleStartTime('');
              setQuickScheduleEndTime('');
              setQuickScheduleMsg('');
            }}
            onUploadClick={() => {
              setFile(null);
              setFilePreview(null);
              setIsUploadModalOpen(true);
            }}
            onRefresh={() => refreshLibrary(true)}
            projectId={getActiveProjectId()}
            videoPreviewRef={videoPreviewRef}
            contentFilter={contentFilter}
            setContentFilter={setContentFilter}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            isLibraryLoading={isLibraryLoading}
          />
        ) : isLibraryLoading ? (
          <div className="media__loading">Loading content…</div>
        ) : filteredItems.length === 0 ? (
          <div className="media__empty">
            <h3 className="media__empty-title">
              {searchQuery
                ? `No assets match "${searchQuery}"`
                : contentFilter === 'videos'
                ? 'No videos in your library yet'
                : contentFilter === 'photos'
                ? 'No images in your library yet'
                : 'Your library is empty'}
            </h3>
            <p className="media__empty-body">
              {searchQuery
                ? 'Try a different keyword, or clear the search.'
                : 'Upload brand assets, brochures, floor plans, and gallery shots to make them available for scheduling.'}
            </p>
          </div>
        ) : (
          // Always flat grid — no section headers. Type buttons handle filtering.
          <div className="media__grid">
            {filteredItems.map((item) => renderMediaCard(item, {
              isSelected: selectedItems.has(item.id),
              isHovered: hoveredCard === item.id,
              isLive: !!(item.contentId && scheduleByContentId[item.contentId]),
              onEnter: () => setHoveredCard(item.id),
              onLeave: () => {
                if (videoPreviewRef.current) videoPreviewRef.current.pause();
                setHoveredCard(null);
              },
              onToggle: () => handleToggleSelection(item.id),
              onPreview: setPreviewItem,
              onSchedule: (e) => {
                e.stopPropagation();
                setQuickScheduleItem(item);
                setQuickScheduleStartDate(new Date().toISOString().slice(0, 10));
                setQuickScheduleEndDate('');
                setQuickScheduleStartTime('');
                setQuickScheduleEndTime('');
                setQuickScheduleMsg('');
              },
              videoPreviewRef,
            }))}
          </div>
        )}
      </main>

      {/* Floating Action Bar */}
      {selectedItems.size > 0 && (
        <div style={floatingActionBarStyle}>
          <div style={selectedCountStyle}>
            <span style={countBadgeStyle}>{selectedItems.size}</span>
            <span style={selectedTextStyle}>Selected</span>
          </div>
          <div style={actionButtonsStyle}>
            <button
              type="button"
              style={actionButtonStyle}
              onClick={handleShare}
              title="Share selected items"
            >
              <ShareIcon size={18} color="#ffffff" />
              <span style={actionLabelStyle}>Share</span>
            </button>
            <button
              type="button"
              style={actionButtonStyle}
              onClick={handleDownload}
              title="Download selected items"
            >
              <DownloadIcon size={18} color="#ffffff" />
              <span style={actionLabelStyle}>Download</span>
            </button>
            <button
              type="button"
              style={actionButtonStyle}
              onClick={handleOpenAddToPlaylist}
              title="Add selected items to a playlist"
            >
              <ListIcon size={18} color="#ffffff" />
              <span style={actionLabelStyle}>Add to Playlist</span>
            </button>
          </div>
        </div>
      )}

      {/* Duplicate-title upload warning modal */}
      {duplicateUploadConflict && duplicateUploadConflict !== 'confirmed' && (
        <div style={newModalOverlayStyle} onClick={() => setDuplicateUploadConflict(null)}>
          <div style={addToPlaylistModalContentStyle} onClick={(e) => e.stopPropagation()}>
            <div style={newModalHeaderStyle}>
              <h2 style={newModalTitleStyle}>Duplicate creative</h2>
            </div>
            <div style={{ padding: '24px 32px' }}>
              {duplicateUploadConflict.batchConflicts ? (
                <p style={{ fontSize: '14px', color: '#475569', margin: 0 }}>
                  One or more files share titles with existing library items:{' '}
                  <strong>
                    {duplicateUploadConflict.batchConflicts
                      .map((c) => c.existing.title || c.existing.name)
                      .join(', ')}
                  </strong>.
                </p>
              ) : (
                <p style={{ fontSize: '14px', color: '#475569', margin: 0 }}>
                  A creative titled{' '}
                  <strong>{duplicateUploadConflict.title || duplicateUploadConflict.name}</strong>{' '}
                  already exists in the library.
                </p>
              )}
            </div>
            <div style={{ ...addToPlaylistActionsStyle, padding: '0 32px 24px' }}>
              <button
                type="button"
                style={addToPlaylistCancelStyle}
                onClick={() => setDuplicateUploadConflict(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                style={addToPlaylistSubmitStyle}
                onClick={() => {
                  setDuplicateUploadConflict('confirmed');
                  if (duplicateUploadConflict.batchConflicts) {
                    handleBatchUpload();
                  } else {
                    handleUpload();
                  }
                }}
              >
                Upload anyway
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add to Playlist Modal */}
      {showAddToPlaylistModal && (
        <div style={newModalOverlayStyle} onClick={handleCloseAddToPlaylistModal}>
          <div style={addToPlaylistModalContentStyle} onClick={(e) => e.stopPropagation()}>
            <div style={newModalHeaderStyle}>
              <h2 style={newModalTitleStyle}>Add to Playlist</h2>
              <button style={newModalCloseButtonStyle} onClick={handleCloseAddToPlaylistModal}>
                <CloseIcon size={20} color="#94a3b8" />
              </button>
            </div>
            <form onSubmit={handleAddToPlaylistSubmit} style={addToPlaylistFormStyle}>
              <div style={addToPlaylistFieldStyle}>
                <label style={addToPlaylistLabelStyle}>Name *</label>
                <input
                  type="text"
                  value={playlistName}
                  onChange={(e) => setPlaylistName(e.target.value)}
                  placeholder="Playlist name"
                  style={addToPlaylistInputStyle}
                  disabled={addToPlaylistSaving}
                  autoFocus
                />
              </div>
              <div style={addToPlaylistFieldStyle}>
                <label style={addToPlaylistLabelStyle}>Description</label>
                <textarea
                  value={playlistDescription}
                  onChange={(e) => setPlaylistDescription(e.target.value)}
                  placeholder="Optional description"
                  rows={3}
                  style={{ ...addToPlaylistInputStyle, resize: 'vertical' }}
                  disabled={addToPlaylistSaving}
                />
              </div>
              {addToPlaylistError && (
                <p style={{ color: '#dc2626', fontSize: '14px', marginBottom: '12px' }}>{addToPlaylistError}</p>
              )}
              {addToPlaylistSuccess && (
                <p style={{ color: '#16a34a', fontSize: '14px', marginBottom: '12px' }}>{addToPlaylistSuccess}</p>
              )}
              <div style={addToPlaylistActionsStyle}>
                <button type="button" style={addToPlaylistCancelStyle} onClick={handleCloseAddToPlaylistModal} disabled={addToPlaylistSaving}>
                  Cancel
                </button>
                <button type="submit" style={addToPlaylistSubmitStyle} disabled={addToPlaylistSaving}>
                  {addToPlaylistSaving ? 'Creating…' : 'Create Playlist'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Quick Schedule Modal — Tier-3 redesign (ScheduleEventModal) */}
      <ScheduleEventModal
        item={quickScheduleItem}
        tvs={tvs}
        open={!!quickScheduleItem}
        onClose={() => {
          setQuickScheduleItem(null);
          setQuickScheduleScreenIds([]);
          setQuickScheduleStartTime('');
          setQuickScheduleEndTime('');
          setQuickScheduleEndDate('');
          setQuickScheduleMsg('');
        }}
        onScheduled={() => {
          // Refresh library so the user sees their new schedule applied
          refreshLibrary(true);
        }}
      />

      {/* Upload Modal */}
      {isUploadModalOpen && (
        <div className="upload-modal-overlay" onClick={handleCloseModal}>
          <div className="upload-modal" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="upload-modal__header">
              <div className="upload-modal__header-text">
                <p className="upload-modal__eyebrow">Media library</p>
                <h2 className="upload-modal__title">Upload content</h2>
                <p className="upload-modal__subtitle">
                  Add brand assets, brochures, and videos to your library.
                  Schedule them to screens when you&apos;re ready.
                </p>
              </div>
              <button
                type="button"
                className="upload-modal__close"
                onClick={handleCloseModal}
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>

            {/* Split Content */}
            <div className="upload-modal__body">
              {/* Left Side - Preview */}
              <div className="upload-modal__left">
                <div
                  className={`upload-modal__dropzone${isDragging ? ' upload-modal__dropzone--dragging' : ''}`}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={() => document.getElementById('file-input-hidden')?.click()}
                  role="button"
                  tabIndex={0}
                >
                  {filePreview?.type === 'image' ? (
                    <img src={filePreview.url} alt="Preview" className="upload-modal__dropzone-preview" />
                  ) : filePreview?.type === 'video' ? (
                    <video src={filePreview.url} className="upload-modal__dropzone-preview" controls />
                  ) : (
                    <>
                      <div className="upload-modal__dropzone-icon">
                        <Plus size={28} strokeWidth={1.5} />
                      </div>
                      <h3 className="upload-modal__dropzone-title">Drop file here, or click to browse</h3>
                      <p className="upload-modal__dropzone-meta">
                        JPG, PNG, MP4, WebP · up to 100 MB · drop multiple files for batch upload
                      </p>
                    </>
                  )}
                  <input
                    id="file-input-hidden"
                    key={fileInputKey}
                    type="file"
                    multiple
                    style={{ display: 'none' }}
                    onChange={handleFileChange}
                    disabled={isUploading}
                    accept={
                      screensaverOnly ||
                      section === 'screensaver' ||
                      contentType === 'screensaver'
                        ? 'image/*'
                        : 'image/*,video/*'
                    }
                  />
                </div>
                <div className="upload-modal__callout">
                  {screenResolution
                    ? `Target screen is ${screenResolution.width}×${screenResolution.height} (${screenResolution.aspectRatio}) — matching assets render edge-to-edge.`
                    : screensaverOnly || section === 'screensaver' || contentType === 'screensaver'
                      ? 'Screensaver accepts images only. 1920×1080 or higher renders cleanly on HD displays.'
                      : '1920×1080 or higher is recommended for HD displays. Pick a target screen above to validate aspect ratio.'}
                </div>
                {/* Multi-file card grid */}
                {pendingFiles.length > 0 && (() => {
                  const totalBytes = pendingFiles.reduce((acc, pf) => acc + (pf.file?.size || 0), 0);
                  const validCount = pendingFiles.length - pendingFailedCount - (pendingFiles.filter((pf) => !!pf.aspectRatioError).length);
                  const mismatchCount = pendingFiles.filter((pf) => !!pf.aspectRatioError).length;
                  return (
                    <div style={{ marginTop: 10 }}>
                      {/* Summary bar */}
                      <div style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        padding: '8px 12px', marginBottom: 8,
                        background: mismatchCount > 0 ? '#fef2f2' : '#f9fafb',
                        border: `1px solid ${mismatchCount > 0 ? '#fecaca' : '#e5e7eb'}`,
                        borderRadius: 6, fontSize: 12,
                      }}>
                        <div style={{ color: '#374151' }}>
                          <strong>{pendingFiles.length}</strong> file{pendingFiles.length === 1 ? '' : 's'} selected
                          {pendingValidatingCount > 0 && <span style={{ color: '#6b7280' }}> · {pendingValidatingCount} checking</span>}
                          {validCount > 0 && <span style={{ color: '#059669' }}> · {validCount} valid</span>}
                          {mismatchCount > 0 && <span style={{ color: '#dc2626' }}> · {mismatchCount} aspect mismatch</span>}
                          {pendingDoneCount > 0 && <span style={{ color: '#2563eb' }}> · {pendingDoneCount} uploaded</span>}
                          {pendingFailedCount > 0 && <span style={{ color: '#dc2626' }}> · {pendingFailedCount} failed</span>}
                        </div>
                        <div style={{ color: '#6b7280' }}>
                          {(totalBytes / 1024 / 1024).toFixed(1)} MB
                          {batchProgress && <span> · {batchProgress.completed}/{batchProgress.total}</span>}
                        </div>
                      </div>
                      {/* Card grid */}
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
                        gap: 12,
                        maxHeight: 420,
                        overflowY: 'auto',
                        padding: 2,
                      }}>
                        {pendingFiles.map((pf) => {
                          const isMismatch = !!pf.aspectRatioError;
                          const isDone = pf.uploadStatus === 'done';
                          const isFailed = pf.uploadStatus === 'failed';
                          const isUploadingCard = pf.uploadStatus === 'uploading';
                          const borderColor = isMismatch
                            ? '#ef4444'
                            : isFailed
                              ? '#ef4444'
                              : isUploadingCard
                                ? '#f59e0b'
                                : isDone
                                  ? '#10b981'
                                  : '#e5e7eb';
                          const dims = pf.dimensions;
                          const arStr = dims && pf.aspectRatio ? pf.aspectRatio.actual : null;
                          return (
                            <div
                              key={pf.id}
                              style={{
                                border: `${isMismatch || isFailed ? '2px' : '1px'} solid ${borderColor}`,
                                borderRadius: 8,
                                padding: 10,
                                background: '#fff',
                                opacity: isDone ? 0.6 : 1,
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 8,
                                position: 'relative',
                              }}
                            >
                              {/* Thumbnail */}
                              <div style={{
                                width: '100%',
                                aspectRatio: '16 / 9',
                                background: '#f3f4f6',
                                borderRadius: 6,
                                overflow: 'hidden',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                position: 'relative',
                              }}>
                                {pf.thumbnailLoading ? (
                                  <div style={{ fontSize: 11, color: '#9ca3af' }}>Loading…</div>
                                ) : pf.thumbnailUrl ? (
                                  <>
                                    <img
                                      src={pf.thumbnailUrl}
                                      alt={pf.title}
                                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                    />
                                    {pf.mediaType === 'video' && (
                                      <div style={{
                                        position: 'absolute', top: 6, right: 6,
                                        background: 'rgba(0,0,0,0.65)', color: '#fff',
                                        padding: '2px 6px', borderRadius: 4, fontSize: 10,
                                        display: 'flex', alignItems: 'center', gap: 3,
                                      }}>
                                        <VideoIcon size={10} color="#fff" /> VIDEO
                                      </div>
                                    )}
                                  </>
                                ) : (
                                  <div style={{ fontSize: 11, color: '#9ca3af', textAlign: 'center', padding: 8 }}>
                                    {pf.mediaType === 'video' ? <VideoIcon size={24} color="#9ca3af" /> : <ImageIcon size={24} color="#9ca3af" />}
                                    <div>{pf.file.name.split('.').pop()?.toUpperCase()}</div>
                                  </div>
                                )}
                              </div>

                              {/* Editable title */}
                              <input
                                type="text"
                                value={pf.title}
                                onChange={(e) => updatePendingFile(pf.id, { title: e.target.value })}
                                disabled={isUploading || isDone}
                                style={{
                                  width: '100%',
                                  fontSize: 12,
                                  padding: '4px 6px',
                                  border: '1px solid #d1d5db',
                                  borderRadius: 4,
                                  fontFamily: 'inherit',
                                  boxSizing: 'border-box',
                                }}
                                placeholder="Title"
                              />

                              {/* Meta line */}
                              <div style={{ fontSize: 10, color: '#6b7280', lineHeight: 1.4 }}>
                                {dims
                                  ? <>{dims.width}×{dims.height}{arStr ? ` · ${arStr}` : ''}</>
                                  : <>Dimensions unavailable</>
                                }
                                {' · '}
                                {(pf.file.size / 1024 / 1024).toFixed(1)} MB
                              </div>

                              {/* Aspect ratio mismatch helper */}
                              {isMismatch && (
                                <div style={{ fontSize: 10, color: '#dc2626', lineHeight: 1.3 }}>
                                  ⚠ {pf.aspectRatioError}
                                </div>
                              )}
                              {!dims && !pf.thumbnailLoading && (
                                <div style={{ fontSize: 10, color: '#d97706', lineHeight: 1.3 }}>
                                  Will upload without aspect-ratio validation
                                </div>
                              )}
                              {!screenResolution && dims && (
                                <div style={{ fontSize: 10, color: '#6b7280', lineHeight: 1.3 }}>
                                  No target screen — validation skipped
                                </div>
                              )}

                              {/* Status + remove row */}
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 'auto' }}>
                                {/* Status badge */}
                                {pf.uploadStatus === 'validating' && (
                                  <span style={{ fontSize: 10, color: '#6b7280' }}>Checking…</span>
                                )}
                                {pf.uploadStatus === 'pending' && !isMismatch && (
                                  <span style={{ fontSize: 10, color: '#374151', background: '#f3f4f6', padding: '2px 6px', borderRadius: 4 }}>Ready</span>
                                )}
                                {pf.uploadStatus === 'pending' && isMismatch && (
                                  <span style={{ fontSize: 10, color: '#dc2626', background: '#fee2e2', padding: '2px 6px', borderRadius: 4 }}>Blocked</span>
                                )}
                                {isUploadingCard && (
                                  <span style={{ fontSize: 10, color: '#d97706', background: '#fef3c7', padding: '2px 6px', borderRadius: 4 }}>Uploading…</span>
                                )}
                                {isDone && (
                                  <span style={{ fontSize: 10, color: '#047857', background: '#d1fae5', padding: '2px 6px', borderRadius: 4 }}>✓ Uploaded</span>
                                )}
                                {isFailed && (
                                  <span
                                    style={{ fontSize: 10, color: '#dc2626', background: '#fee2e2', padding: '2px 6px', borderRadius: 4, cursor: 'help' }}
                                    title={pf.uploadError || 'Upload failed'}
                                  >
                                    ✗ Failed
                                  </span>
                                )}
                                <button
                                  type="button"
                                  style={{
                                    border: 'none',
                                    background: 'none',
                                    cursor: (isUploading || isDone) ? 'not-allowed' : 'pointer',
                                    color: '#ef4444',
                                    fontSize: 16,
                                    padding: '0 4px',
                                    opacity: (isUploading || isDone) ? 0.3 : 1,
                                  }}
                                  onClick={() => {
                                    if (isUploading || isDone) return;
                                    if (pf.thumbnailRevocable && pf.thumbnailUrl) {
                                      try { URL.revokeObjectURL(pf.thumbnailUrl); } catch { /* noop */ }
                                    }
                                    setPendingFiles((prev) => prev.filter((x) => x.id !== pf.id));
                                  }}
                                  disabled={isUploading || isDone}
                                  aria-label={`Remove ${pf.title}`}
                                >
                                  ×
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}
                {/* Screen Preview - shows how content will look on the target screen */}
                {file && screenResolution && !aspectRatioError && (
                  <div style={{ marginTop: 12 }}>
                    <ScreenPreview
                      screenWidth={screenResolution.width}
                      screenHeight={screenResolution.height}
                      media={file}
                      maxPreviewHeight={200}
                      calibration={screenCalibration}
                    />
                  </div>
                )}
              </div>

              {/* Right Side - Form */}
              <div className="upload-modal__right">
                <div style={formFieldsContainerStyle}>
                  {/* Target Screen Selector */}
                  <div style={formFieldStyle}>
                    <label style={formLabelStyle} htmlFor="target-screen">
                      Target screen <span className="upload-modal__optional">(optional)</span>
                    </label>
                    <p className="upload-modal__hint upload-modal__hint--above">
                      Pick a screen to preview how this asset will look and validate
                      its aspect ratio against that screen. Leave blank to upload to
                      the library without validation.
                    </p>
                    {(() => {
                      const selectedDevice = tvs.find(tv => (tv.deviceId || tv.tvId) === selectedTvId);
                      const getLabel = (tv) => getDeviceCompactLabel(tv);
                      return (
                        <div style={{ position: 'relative' }}>
                          <input
                            id="target-screen"
                            type="text"
                            style={formInputStyle}
                            placeholder="Search screens by name, ID, or category..."
                            value={screenSearchQuery !== undefined ? screenSearchQuery : (selectedDevice ? getLabel(selectedDevice) : '')}
                            onChange={(e) => {
                              setScreenSearchQuery(e.target.value);
                              if (e.target.value === '') setSelectedTvId('');
                            }}
                            onFocus={() => {
                              // Always start with a clean empty query on focus so
                              // the full list shows immediately. Previous behavior
                              // preserved stale state if the user had an incomplete
                              // query from a prior open, which read as "no results".
                              setScreenSearchQuery('');
                            }}
                            onBlur={() => setTimeout(() => setScreenSearchQuery(undefined), 200)}
                            disabled={isUploading}
                            // Disable every form of browser-level autofill /
                            // auto-suggest. Chrome/Safari were injecting stale
                            // previously-typed labels that mismatched the live
                            // device list and read as "no results". The unusual
                            // name + autoComplete="new-password" is the most
                            // reliable combo to suppress autofill in 2026.
                            autoComplete="new-password"
                            autoCorrect="off"
                            autoCapitalize="off"
                            spellCheck="false"
                            name="cms_internal_screen_search"
                          />
                          {selectedTvId && screenSearchQuery === undefined && (
                            <button
                              type="button"
                              onClick={() => { setSelectedTvId(''); setScreenSearchQuery(''); }}
                              style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', border: 'none', background: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: 16 }}
                            >×</button>
                          )}
                          {screenSearchQuery !== undefined && (
                            <div style={{
                              position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50,
                              maxHeight: 250, overflowY: 'auto',
                              background: '#fff', border: '1px solid #d1d5db', borderRadius: 6,
                              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                            }}>
                              {(() => {
                                // Normalize: lowercase, strip diacritics, canonicalize
                                // non-ASCII typographic chars (× → x, · → space), treat
                                // punctuation as whitespace, and collapse whitespace.
                                // Both sides of the comparison go through this so
                                // spelling / encoding / separator differences don't
                                // cause false misses.
                                const normalize = (s) => String(s || '')
                                  .normalize('NFD')
                                  .replace(/[\u0300-\u036f]/g, '') // strip combining diacritics
                                  .toLowerCase()
                                  .replace(/×/g, 'x')
                                  .replace(/·/g, ' ')
                                  .replace(/[_\-/\\.]+/g, ' ') // treat punctuation as whitespace
                                  .replace(/\s+/g, ' ')
                                  .trim();
                                const rawQuery = normalize(screenSearchQuery);
                                // Tokenize on whitespace so "convrse wall tv" matches
                                // any device whose label/id/location/resolution contains
                                // all three words, in any order, across any field.
                                const tokens = rawQuery.split(/\s+/).filter(Boolean);
                                const buildGroups = (source) => {
                                  const g = {};
                                  source.forEach((tv) => {
                                    const id = tv.deviceId || tv.tvId || '';
                                    const cat = getScreenCategoryFromDeviceId(id);
                                    if (!g[cat]) g[cat] = [];
                                    g[cat].push(tv);
                                  });
                                  return g;
                                };
                                const matches = tvs.filter((tv) => {
                                  if (tokens.length === 0) return true;
                                  const id = tv.deviceId || tv.tvId || '';
                                  const label = normalize(getLabel(tv));
                                  const cat = getScreenCategoryFromDeviceId(id);
                                  const catLabel = normalize(formatCategoryLabel(cat));
                                  const dims = resolveScreenDims(tv);
                                  const dimsStr = dims ? `${dims.width}x${dims.height} ${dims.width} ${dims.height}` : '';
                                  // Brute-force catch-all: stringify the entire tv
                                  // object and normalize it. If the query token
                                  // exists as a substring of ANY text field on this
                                  // tv (name, id, category, location, profile name,
                                  // model, status, anything), the match succeeds.
                                  // This is deliberately permissive — a picker
                                  // should prefer false positives (user scrolls past
                                  // a few extras) over false negatives (user can't
                                  // find their screen).
                                  let jsonBlob = '';
                                  try { jsonBlob = normalize(JSON.stringify(tv)); } catch { /* ignore cycles */ }
                                  const haystack = [
                                    label,
                                    normalize(id),
                                    catLabel,
                                    normalize(tv.location),
                                    normalize(getDeviceDisplayName(tv)),
                                    normalize(tv.tvId),
                                    normalize(tv.model),
                                    dimsStr,
                                    jsonBlob,
                                  ].join(' ');
                                  return tokens.every((t) => haystack.includes(t));
                                });
                                // "Show all" fallback: when the search returns no
                                // matches, still render the full list below a
                                // clarifying header. The user should ALWAYS be able
                                // to see and pick from the full device list.
                                const noMatches = matches.length === 0 && tokens.length > 0;
                                const displaySource = noMatches ? tvs : matches;
                                const grouped = buildGroups(displaySource);
                                const cats = Object.keys(grouped).sort((a, b) => categorySortKey(a).localeCompare(categorySortKey(b)));
                                if (tvs.length === 0) {
                                  return <div style={{ padding: '10px 12px', fontSize: 12, color: '#9ca3af' }}>No screens registered yet</div>;
                                }
                                const noMatchHeader = noMatches ? (
                                  <div style={{ padding: '8px 12px', fontSize: 11, color: '#b45309', background: '#fffbeb', borderBottom: '1px solid #fde68a', lineHeight: 1.4 }}>
                                    No screens match &ldquo;{screenSearchQuery}&rdquo;. Showing all <strong>{tvs.length}</strong> registered screens:
                                  </div>
                                ) : null;
                                return (
                                  <>
                                    {noMatchHeader}
                                    {cats.map((cat) => (
                                  <div key={cat}>
                                    <div style={{ padding: '6px 12px', fontSize: 10, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', background: '#f9fafb', borderBottom: '1px solid #f3f4f6' }}>
                                      {formatCategoryLabel(cat)} — {grouped[cat].length}
                                    </div>
                                    {grouped[cat].map((tv) => {
                                      const id = tv.deviceId || tv.tvId;
                                      return (
                                        <div
                                          key={id}
                                          onMouseDown={() => { setSelectedTvId(id); setScreenSearchQuery(undefined); }}
                                          style={{
                                            padding: '8px 12px', cursor: 'pointer', fontSize: 13,
                                            background: id === selectedTvId ? '#eff6ff' : '#fff',
                                            borderBottom: '1px solid #f9fafb',
                                          }}
                                          onMouseEnter={(e) => e.currentTarget.style.background = '#f3f4f6'}
                                          onMouseLeave={(e) => e.currentTarget.style.background = id === selectedTvId ? '#eff6ff' : '#fff'}
                                        >
                                          <div style={{ fontWeight: 500 }}>{getDeviceDisplayName(tv)}</div>
                                          <div style={{ fontSize: 11, color: '#9ca3af', display: 'flex', gap: 8 }}>
                                            <span style={{ fontFamily: 'ui-monospace, monospace' }}>{id}</span>
                                            {(() => {
                                              const dd = resolveScreenDims(tv);
                                              return dd ? <span>{dd.width}×{dd.height}</span> : null;
                                            })()}
                                            <span style={{ color: tv.status === 'online' ? '#10b981' : '#ef4444' }}>{tv.status}</span>
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                    ))}
                                  </>
                                );
                              })()}
                            </div>
                          )}
                        </div>
                      );
                    })()}
                    {screenResolution && (
                      <div style={{
                        marginTop: 6,
                        padding: '6px 10px',
                        background: '#f0fdf4',
                        border: '1px solid #bbf7d0',
                        borderRadius: 6,
                        fontSize: 12,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                      }}>
                        <span style={{ fontWeight: 600, color: '#166534' }}>
                          {screenResolution.width} × {screenResolution.height}
                        </span>
                        <span style={{
                          background: screenResolution.width < screenResolution.height ? '#ede9fe' : '#dbeafe',
                          color: screenResolution.width < screenResolution.height ? '#7c3aed' : '#2563eb',
                          padding: '1px 6px',
                          borderRadius: 3,
                          fontSize: 10,
                          fontWeight: 600,
                        }}>
                          {screenResolution.width < screenResolution.height ? 'Portrait' : screenResolution.width > screenResolution.height ? 'Landscape' : 'Square'}
                        </span>
                        <span style={{ color: '#6b7280', fontSize: 11 }}>
                          {screenResolution.aspectRatio}
                        </span>
                        <span style={{ color: '#6b7280', fontSize: 11 }}>
                          — upload content matching this resolution
                        </span>
                      </div>
                    )}
                  </div>

                  <div style={formFieldStyle}>
                    <label style={formLabelStyle} htmlFor="title">
                      Title
                    </label>
                    <input
                      id="title"
                      type="text"
                      style={formInputStyle}
                      value={title}
                      onChange={(event) => setTitle(event.target.value)}
                      placeholder="e.g., Marketing Brochure 2024"
                      disabled={isUploading}
                    />
                  </div>

                  <div style={formFieldStyle}>
                    <label style={formLabelStyle} htmlFor="category">
                      Category <span style={{ color: '#dc2626' }}>*</span>
                    </label>
                    <select
                      id="category"
                      style={formInputStyle}
                      value={category || ''}
                      onChange={(event) => setCategory(event.target.value || null)}
                      disabled={isUploading}
                    >
                      <option value="">Select a category…</option>
                      {CONTENT_CATEGORIES.map((c) => (
                        <option key={c.key} value={c.key}>{c.label}</option>
                      ))}
                    </select>
                    {category && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6, fontSize: 11, color: '#6b7280' }}>
                        <span style={{ width: 10, height: 10, borderRadius: 2, background: getCategory(category)?.color || '#9ca3af', display: 'inline-block' }} />
                        Applies to every file in this upload.
                      </div>
                    )}
                  </div>

                  <div style={formFieldStyle}>
                    <label style={formLabelStyle} htmlFor="section">
                      Section
                    </label>
                    <select
                      id="section"
                      style={formInputStyle}
                      value={section}
                      onChange={handleSectionChange}
                      disabled={isUploading || screensaverOnly}
                    >
                      {SECTION_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <p className="upload-modal__hint">
                      Where this asset lives in the TV app. Media type (image or
                      video) is detected automatically from the file.
                    </p>
                  </div>

                  {/* Content expiry — flipped from "Permanent content" to
                      "Set an expiry date" so the default (permanent) needs no
                      extra explanation. Backend state stays isPermanent for
                      compatibility; this is purely a UI inversion.
                      Expiry inputs are datetime-local so marketers can pick a
                      specific time-of-day, not just a calendar day. Backend
                      content schema stores valid_from/valid_until as Date
                      objects and `new Date('YYYY-MM-DDTHH:mm')` parses correctly
                      with local timezone. */}
                  <div className="upload-modal__expiry">
                    <label className="upload-modal__toggle">
                      <input
                        type="checkbox"
                        checked={!isPermanent}
                        onChange={(e) => setIsPermanent(!e.target.checked)}
                        disabled={isUploading}
                      />
                      <span>
                        Set an expiry date &amp; time
                        <span className="upload-modal__toggle-hint">
                          Remove this asset from all schedules after a specific
                          moment (e.g. end of a promotion, launch-day cutoff).
                        </span>
                      </span>
                    </label>
                    {!isPermanent && (
                      <div className="upload-modal__expiry-dates">
                        <div>
                          <label className="upload-modal__sublabel">
                            Valid from <span className="upload-modal__optional">(optional)</span>
                          </label>
                          <input
                            type="datetime-local"
                            style={formInputStyle}
                            value={validFrom}
                            onChange={(e) => setValidFrom(e.target.value)}
                            disabled={isUploading}
                          />
                        </div>
                        <div>
                          <label className="upload-modal__sublabel">
                            Valid until <span className="upload-modal__required">*</span>
                          </label>
                          <input
                            type="datetime-local"
                            style={formInputStyle}
                            value={validUntil}
                            onChange={(e) => setValidUntil(e.target.value)}
                            disabled={isUploading}
                            min={validFrom || new Date().toISOString().slice(0, 16)}
                          />
                        </div>
                      </div>
                    )}
                    {!isPermanent && validUntil && (
                      <p className="upload-modal__expiry-note">
                        Auto-removes from all schedules on{' '}
                        <strong>
                          {(() => {
                            try {
                              const d = new Date(validUntil);
                              if (Number.isNaN(d.getTime())) return validUntil;
                              return d.toLocaleString(undefined, {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                                hour: 'numeric',
                                minute: '2-digit',
                              });
                            } catch {
                              return validUntil;
                            }
                          })()}
                        </strong>.
                      </p>
                    )}
                  </div>

                  {/* Schedule-after-upload removed: users schedule from the
                      library card after upload. Simpler, clearer decisions. */}

                </div>
              </div>
            </div>

            {/* Bottom resolution banner removed — the callout under the
                dropzone already shows target screen dims. Kept the AR error
                banner since it's a hard gate. */}
            {aspectRatioError && (
              <div style={{ padding: '10px 24px', fontSize: '13px', color: '#dc2626', background: '#fef2f2', borderTop: '1px solid #fecaca', fontWeight: 500 }}>
                {aspectRatioError}
              </div>
            )}

            {/* Footer */}
            <div className="upload-modal__footer">
              <div />
              <div className="upload-modal__footer-actions">
                <Button variant="secondary" size="md" onClick={handleCloseModal}>
                  Cancel
                </Button>
                {(() => {
                  const multi = pendingFiles.length > 0;
                  const multiDisabled =
                    multi && (hasAnyAspectMismatch || pendingValidatingCount > 0 || (pendingReadyCount === 0 && pendingFailedCount === 0));
                  const singleDisabled = !multi && (!file || !!aspectRatioError);
                  // aspectRatioError is the single-file gate but the bottom
                  // banner renders on it regardless of mode — if it's shown
                  // the button must be disabled in every mode.
                  const disabled = isUploading || (!file && !multi) || multiDisabled || singleDisabled || !!aspectRatioError;

                  let label;
                  if (isUploading) {
                    label = batchProgress ? `Uploading ${batchProgress.completed}/${batchProgress.total}…` : 'Uploading…';
                  } else if (multi) {
                    if (pendingFailedCount > 0 && pendingReadyCount === 0) {
                      label = `Retry ${pendingFailedCount} failed`;
                    } else if (pendingFailedCount > 0) {
                      label = `Upload ${pendingReadyCount + pendingFailedCount} files`;
                    } else {
                      label = `Upload ${pendingReadyCount} file${pendingReadyCount === 1 ? '' : 's'}`;
                    }
                  } else {
                    label = 'Upload to library';
                  }

                  const title = multi && hasAnyAspectMismatch
                    ? 'Remove or replace files with aspect ratio mismatches to continue.'
                    : undefined;

                  return (
                    <Button
                      variant="primary"
                      size="md"
                      loading={isUploading}
                      disabled={disabled}
                      onClick={handleUpload}
                      title={title}
                    >
                      {label}
                    </Button>
                  );
                })()}
              </div>
            </div>

            {statusMessage && <div style={statusMessageStyle}>{statusMessage}</div>}
            {errorMessage && <div style={errorMessageStyle}>{errorMessage}</div>}
            {successMessage && <div style={successMessageStyle}>{successMessage}</div>}
          </div>
        </div>
      )}
      <AssetPreviewModal item={previewItem} onClose={() => setPreviewItem(null)} />
    </div>
  );
};

const newContainerStyle = {
  width: '100%',
};

const mainContentStyle = {
  width: '100%',
};

/* eslint-disable no-unused-vars */
// TODO (cleanup): many of the inline style constants below were orphaned by
// the media-library redesign that moved the header/toolbar/grid/card onto
// CSS classes in media.css. They're kept for now because the file still has
// ~100 other inline styles that ARE used (upload modal, floating action bar,
// schedule modal wrappers, etc.) and surgically deleting just the orphans
// from this tangled 1000-line styles dump is its own follow-up. Any named
// *Style variable below that isn't referenced in JSX is a candidate.
const tabsAndActionsBarStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: '24px',
  flexWrap: 'wrap',
  gap: '16px',
};

const tabsContainerStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '24px',
};

const tabButtonStyle = {
  fontSize: '14px',
  fontWeight: '500',
  color: '#64748b',
  backgroundColor: 'transparent',
  border: 'none',
  cursor: 'pointer',
  padding: '8px 0',
  borderBottom: '2px solid transparent',
  transition: 'all 0.2s',
};

const activeTabButtonStyle = {
  ...tabButtonStyle,
  color: '#0f172a',
  fontWeight: '600',
  borderBottom: '2px solid #c8a147',
};

const actionsContainerStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
};

const createDesignButtonStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '8px 16px',
  fontSize: '14px',
  fontWeight: '500',
  color: '#475569',
  backgroundColor: '#ffffff',
  border: '1px solid #e2e8f0',
  borderRadius: '8px',
  cursor: 'pointer',
  transition: 'all 0.2s',
};

const createPlaylistButtonStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '8px 20px',
  fontSize: '14px',
  fontWeight: '500',
  backgroundColor: '#ffffff',
  color: '#b8913f',
  border: '1px solid #c8a147',
  borderRadius: '8px',
  cursor: 'pointer',
  boxShadow: '0 2px 4px rgba(200, 161, 71, 0.15)',
  transition: 'all 0.2s',
};

const uploadMediaButtonStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '8px 20px',
  fontSize: '14px',
  fontWeight: '500',
  backgroundColor: '#c8a147',
  color: '#ffffff',
  border: 'none',
  borderRadius: '8px',
  cursor: 'pointer',
  boxShadow: '0 4px 6px rgba(200, 161, 71, 0.2)',
  transition: 'all 0.2s',
};

const sectionHeaderStyle = {
  marginBottom: '24px',
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: '16px',
};

const sectionTitleStyle = {
  fontSize: '20px',
  fontWeight: '700',
  color: '#0f172a',
  margin: '0 0 4px 0',
  letterSpacing: '-0.3px',
};

const sectionSubtitleStyle = {
  fontSize: '12px',
  fontWeight: '600',
  color: '#64748b',
  textTransform: 'uppercase',
  letterSpacing: '0.1em',
  margin: 0,
};

const controlsBarStyle = {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  gap: '16px',
  marginBottom: '24px',
};

const filterContainerStyle = {
  display: 'flex',
  alignItems: 'center',
  backgroundColor: '#ffffff',
  padding: '6px',
  borderRadius: '12px',
  border: '1px solid #e2e8f0',
  boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
  width: 'fit-content',
};

const filterButtonStyle = {
  padding: '6px 20px',
  fontSize: '14px',
  fontWeight: '500',
  color: '#64748b',
  backgroundColor: 'transparent',
  border: 'none',
  borderRadius: '8px',
  cursor: 'pointer',
  transition: 'all 0.2s',
};

const activeFilterButtonStyle = {
  ...filterButtonStyle,
  backgroundColor: '#c8a147',
  color: '#ffffff',
  fontWeight: '600',
  boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
};

const rightControlsBarStyle = {
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: '12px',
};

const searchContainerStyle = {
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
};

const searchIconStyle = {
  position: 'absolute',
  left: '12px',
  display: 'flex',
  alignItems: 'center',
  pointerEvents: 'none',
};

const newSearchInputStyle = {
  paddingLeft: '40px',
  paddingRight: '16px',
  paddingTop: '8px',
  paddingBottom: '8px',
  width: '256px',
  fontSize: '14px',
  backgroundColor: '#ffffff',
  border: '1px solid #e2e8f0',
  borderRadius: '8px',
  outline: 'none',
  transition: 'all 0.2s',
};

const newSortButtonStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '8px 16px',
  fontSize: '14px',
  fontWeight: '500',
  backgroundColor: '#ffffff',
  border: '1px solid #e2e8f0',
  borderRadius: '8px',
  color: '#475569',
  cursor: 'pointer',
  transition: 'all 0.2s',
};

const sortIconStyle = {
  fontSize: '18px',
};

const dropdownIconStyle = {
  fontSize: '18px',
};

const dividerStyle = {
  height: '32px',
  width: '1px',
  backgroundColor: '#e2e8f0',
};

const newFolderIconButtonStyle = {
  padding: '8px',
  backgroundColor: 'rgba(200, 161, 71, 0.1)',
  color: '#c8a147',
  border: 'none',
  borderRadius: '8px',
  cursor: 'pointer',
  fontSize: '20px',
  transition: 'all 0.2s',
};

const viewToggleContainerStyle = {
  display: 'flex',
  alignItems: 'center',
  backgroundColor: '#ffffff',
  border: '1px solid #e2e8f0',
  borderRadius: '8px',
  padding: '2px',
};

const viewToggleStyle = {
  padding: '6px',
  borderRadius: '6px',
  backgroundColor: 'transparent',
  border: 'none',
  color: '#94a3b8',
  cursor: 'pointer',
  fontSize: '18px',
  transition: 'all 0.2s',
};

const activeViewToggleStyle = {
  ...viewToggleStyle,
  backgroundColor: '#f1f5f9',
  color: '#c8a147',
};

const mediaFilesSectionStyle = {
  padding: '24px',
  backgroundColor: '#ffffff',
};

const mediaFilesHeaderStyle = {
  marginBottom: '20px',
};

const mediaFilesTitleStyle = {
  fontSize: '24px',
  fontWeight: '700',
  color: '#111827',
  margin: '0 0 4px 0',
};

const mediaFilesSubtitleStyle = {
  fontSize: '14px',
  color: '#6b7280',
  margin: 0,
};

const mediaFilesControlsStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: '12px',
  marginTop: '16px',
};

const filterPillsStyle = {
  display: 'flex',
  gap: '8px',
};

const filterPillStyle = {
  padding: '6px 16px',
  fontSize: '13px',
  fontWeight: '600',
  backgroundColor: '#ffffff',
  color: '#6b7280',
  border: '1px solid #e5e7eb',
  borderRadius: '20px',
  cursor: 'pointer',
};

const activeFilterPillStyle = {
  ...filterPillStyle,
  backgroundColor: '#2563eb',
  color: '#ffffff',
  border: '1px solid #2563eb',
};

const rightControlsStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  flexWrap: 'wrap',
};

const sortButtonStyle = {
  padding: '6px 12px',
  fontSize: '13px',
  fontWeight: '600',
  backgroundColor: '#ffffff',
  color: '#374151',
  border: '1px solid #e5e7eb',
  borderRadius: '6px',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
};

const searchInputStyle = {
  padding: '6px 12px',
  fontSize: '13px',
  border: '1px solid #e5e7eb',
  borderRadius: '6px',
  width: '200px',
};

const newFolderButtonStyle = {
  padding: '6px 12px',
  fontSize: '13px',
  fontWeight: '600',
  backgroundColor: '#2563eb',
  color: '#ffffff',
  border: 'none',
  borderRadius: '6px',
  cursor: 'pointer',
};


const newMediaGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
  gap: '24px',
};

const newMediaCardStyle = {
  backgroundColor: '#ffffff',
  borderRadius: '12px',
  overflow: 'hidden',
  boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
  border: '1px solid #e2e8f0',
  transition: 'all 0.3s',
  cursor: 'pointer',
};

const newMediaPreviewStyle = {
  position: 'relative',
  aspectRatio: '4/3',
  backgroundColor: '#f1f5f9',
  overflow: 'hidden',
};

const newMediaImageStyle = {
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  transition: 'transform 0.5s',
};

const hoverOverlayStyle = {
  position: 'absolute',
  inset: 0,
  background: 'linear-gradient(to top, rgba(0, 0, 0, 0.4), transparent)',
  opacity: 0,
  transition: 'opacity 0.3s',
};

const hoverControlsStyle = {
  position: 'absolute',
  inset: 0,
  padding: '16px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  zIndex: 10,
  opacity: 1,
  transition: 'opacity 0.3s',
  pointerEvents: 'none',
};

const hoverCheckboxStyle = {
  width: '20px',
  height: '20px',
  borderRadius: '4px',
  border: '1px solid rgba(255, 255, 255, 0.4)',
  backgroundColor: 'rgba(0, 0, 0, 0.2)',
  cursor: 'pointer',
  accentColor: '#c8a147',
};

const hoverMenuButtonStyle = {
  alignSelf: 'flex-end',
  backgroundColor: 'rgba(0, 0, 0, 0.4)',
  color: '#ffffff',
  border: 'none',
  borderRadius: '4px',
  padding: '4px',
  cursor: 'pointer',
  backdropFilter: 'blur(4px)',
  transition: 'background-color 0.2s',
};

const menuIconStyle = {
  fontSize: '18px',
  display: 'block',
};

const videoDurationStyle = {
  position: 'absolute',
  bottom: '8px',
  right: '8px',
  backgroundColor: 'rgba(0, 0, 0, 0.6)',
  backdropFilter: 'blur(8px)',
  padding: '4px 8px',
  borderRadius: '4px',
  fontSize: '10px',
  fontWeight: '700',
  color: '#ffffff',
  opacity: 0,
  transition: 'opacity 0.3s',
};

const noPreviewCardStyle = {
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '16px',
  backgroundColor: '#f8fafc',
  border: '2px dashed #e2e8f0',
  borderRadius: '8px',
};


const newMediaInfoStyle = {
  padding: '16px',
  borderTop: '1px solid #f1f5f9',
};

const newMediaTitleStyle = {
  fontSize: '14px',
  fontWeight: '600',
  color: '#0f172a',
  margin: '0 0 4px 0',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const newMediaMetaStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  fontSize: '12px',
  color: '#64748b',
};


const addNewMediaButtonStyle = {
  border: '2px dashed #cbd5e1',
  borderRadius: '12px',
  aspectRatio: '4/3',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '12px',
  backgroundColor: 'transparent',
  cursor: 'pointer',
  transition: 'all 0.2s',
};

// Add hover state via onMouseEnter/onMouseLeave in JSX

const addIconContainerStyle = {
  width: '48px',
  height: '48px',
  borderRadius: '50%',
  backgroundColor: '#f1f5f9',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'all 0.2s',
};


const addButtonTextStyle = {
  fontSize: '14px',
  fontWeight: '500',
  color: '#64748b',
};

const floatingActionBarStyle = {
  position: 'fixed',
  bottom: '24px',
  left: '50%',
  transform: 'translateX(-50%)',
  backgroundColor: '#0f172a',
  color: '#ffffff',
  padding: '12px 24px',
  borderRadius: '999px',
  boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
  display: 'flex',
  alignItems: 'center',
  gap: '24px',
  zIndex: 50,
};

const selectedCountStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  paddingRight: '24px',
  borderRight: '1px solid #334155',
};

const countBadgeStyle = {
  width: '24px',
  height: '24px',
  backgroundColor: '#c8a147',
  color: '#ffffff',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '12px',
  fontWeight: '700',
};

const selectedTextStyle = {
  fontSize: '14px',
  fontWeight: '500',
};

const actionButtonsStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
};

const actionButtonStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  backgroundColor: 'transparent',
  border: 'none',
  color: '#ffffff',
  cursor: 'pointer',
  transition: 'color 0.2s',
  padding: 0,
};


const actionLabelStyle = {
  fontSize: '12px',
  fontWeight: '600',
};


const contextMenuDropdownStyle = {
  position: 'absolute',
  top: '32px',
  right: '0',
  backgroundColor: '#ffffff',
  border: '1px solid #e5e7eb',
  borderRadius: '6px',
  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
  minWidth: '200px',
  zIndex: 20,
};

const contextMenuItemStyle = {
  display: 'block',
  width: '100%',
  padding: '10px 12px',
  fontSize: '13px',
  textAlign: 'left',
  backgroundColor: 'transparent',
  border: 'none',
  cursor: 'pointer',
  color: '#374151',
  textDecoration: 'none',
};

const mediaPreviewStyle = {
  width: '100%',
  paddingTop: '140%',
  position: 'relative',
  backgroundColor: '#f3f4f6',
  overflow: 'hidden',
};

const mediaImageStyle = {
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  objectFit: 'cover',
};

const videoPreviewStyle = {
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
};

const videoPlayIconStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '56px',
  height: '56px',
  borderRadius: '50%',
  backgroundColor: 'rgba(37, 99, 235, 0.9)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const noPreviewStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  fontSize: '12px',
  color: '#9ca3af',
};

const mediaInfoStyle = {
  padding: '16px',
};

const mediaTitleStyle = {
  fontSize: '14px',
  fontWeight: '600',
  color: '#111827',
  marginBottom: '6px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const mediaResolutionStyle = {
  fontSize: '12px',
  color: '#6b7280',
};

const newModalOverlayStyle = {
  position: 'fixed',
  inset: 0,
  backgroundColor: 'rgba(15, 23, 42, 0.6)',
  backdropFilter: 'blur(4px)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
  padding: '16px',
};

const newModalContentStyle = {
  backgroundColor: '#ffffff',
  width: '100%',
  maxWidth: '1280px',
  borderRadius: '16px',
  boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  maxHeight: '90vh',
};

const addToPlaylistModalContentStyle = {
  ...newModalContentStyle,
  maxWidth: '420px',
  maxHeight: 'none',
};

const addToPlaylistFormStyle = { padding: '24px 32px 32px', display: 'flex', flexDirection: 'column', gap: '4px' };
const addToPlaylistFieldStyle = { marginBottom: '16px' };
const addToPlaylistLabelStyle = { display: 'block', fontSize: '14px', fontWeight: '600', color: '#334155', marginBottom: '6px' };
const addToPlaylistInputStyle = { width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' };
const addToPlaylistActionsStyle = { display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '8px' };
const addToPlaylistCancelStyle = { padding: '10px 20px', border: '1px solid #e2e8f0', borderRadius: '8px', background: '#fff', fontSize: '14px', cursor: 'pointer' };
const addToPlaylistSubmitStyle = { padding: '10px 20px', borderRadius: '8px', background: '#2563eb', color: '#fff', border: 'none', fontSize: '14px', fontWeight: '600', cursor: 'pointer' };

const newModalHeaderStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '24px 32px',
  borderBottom: '1px solid #f1f5f9',
};

const newModalTitleStyle = {
  fontSize: '20px',
  fontWeight: '700',
  color: '#0f172a',
  margin: 0,
};

const newModalSubtitleStyle = {
  fontSize: '14px',
  color: '#64748b',
  margin: '4px 0 0 0',
};

const newModalCloseButtonStyle = {
  padding: '8px',
  backgroundColor: 'transparent',
  border: 'none',
  borderRadius: '50%',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'background-color 0.2s',
};

// Add hover effect via inline style with onMouseEnter/onMouseLeave
const newModalCloseButtonHoverStyle = {
  ...newModalCloseButtonStyle,
  backgroundColor: '#f1f5f9',
};

const closeIconStyle = {
  fontSize: '20px',
  color: '#94a3b8',
};

const modalSplitContainerStyle = {
  display: 'flex',
  flex: 1,
  overflow: 'hidden',
};

const modalLeftSideStyle = {
  width: '50%',
  flex: 1,
  minHeight: 0,
  padding: '32px',
  borderRight: '1px solid #f1f5f9',
  backgroundColor: '#f8fafc',
  display: 'flex',
  flexDirection: 'column',
};

const previewLabelStyle = {
  fontSize: '14px',
  fontWeight: '600',
  color: '#475569',
  marginBottom: '16px',
  display: 'block',
};

const dropZoneStyle = (isDragging) => ({
  flex: 1,
  border: `2px dashed ${isDragging ? '#2563eb' : '#cbd5e1'}`,
  borderRadius: '12px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '32px',
  textAlign: 'center',
  cursor: 'pointer',
  position: 'relative',
  overflow: 'hidden',
  backgroundColor: '#ffffff',
  transition: 'all 0.2s',
  backgroundImage: isDragging
    ? 'none'
    : 'url("data:image/svg+xml,%3Csvg width=\'100\' height=\'100\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cpath d=\'M50 20L70 50H50V80H30V50H10L50 20Z\' fill=\'%23e2e8f0\' opacity=\'0.3\' transform=\'rotate(-12 50 50)\'/%3E%3C/svg%3E")',
  backgroundSize: '200px',
  backgroundPosition: 'center',
});

const uploadIconContainerStyle = {
  width: '64px',
  height: '64px',
  backgroundColor: '#eff6ff',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: '16px',
  transition: 'transform 0.2s',
};


const dropZoneTitleStyle = {
  fontSize: '18px',
  fontWeight: '600',
  color: '#0f172a',
  marginBottom: '8px',
};

const dropZoneTextStyle = {
  fontSize: '14px',
  color: '#64748b',
  marginBottom: '24px',
};

const browseButtonStyle = {
  padding: '8px 24px',
  backgroundColor: '#ffffff',
  border: '1px solid #e2e8f0',
  color: '#475569',
  borderRadius: '8px',
  fontSize: '14px',
  fontWeight: '500',
  cursor: 'pointer',
  transition: 'all 0.2s',
  boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
};

const previewImageStyle = {
  width: '100%',
  height: '100%',
  objectFit: 'contain',
  position: 'absolute',
  top: 0,
  left: 0,
};

const infoBoxStyle = {
  marginTop: '24px',
  padding: '16px',
  backgroundColor: '#eff6ff',
  borderRadius: '8px',
  border: '1px solid #bfdbfe',
  display: 'flex',
  alignItems: 'flex-start',
  gap: '12px',
};

const infoIconStyle = {
  marginTop: '2px',
  display: 'flex',
  alignItems: 'center',
};

const infoTextStyle = {
  fontSize: '12px',
  color: '#1e40af',
  lineHeight: '1.5',
  margin: 0,
  flex: 1,
};

const modalRightSideStyle = {
  width: '50%',
  flex: 1,
  minHeight: 0,
  padding: '32px',
  overflowY: 'auto',
  backgroundColor: '#ffffff',
};

const formFieldsContainerStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
};

const formFieldStyle = {
  display: 'flex',
  flexDirection: 'column',
};

const formLabelStyle = {
  display: 'block',
  fontSize: '14px',
  fontWeight: '600',
  color: '#475569',
  marginBottom: '8px',
};

const formInputStyle = {
  width: '100%',
  padding: '10px 16px',
  backgroundColor: '#ffffff',
  border: '1px solid #e2e8f0',
  borderRadius: '8px',
  fontSize: '14px',
  color: '#0f172a',
  transition: 'all 0.2s',
  boxSizing: 'border-box',
  outline: 'none',
};

const formInputFocusStyle = {
  ...formInputStyle,
  borderColor: '#2563eb',
  boxShadow: '0 0 0 3px rgba(37, 99, 235, 0.1)',
};

const formGridRowStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(2, 1fr)',
  gap: '16px',
};

const formTextareaStyle = {
  width: '100%',
  padding: '10px 16px',
  backgroundColor: '#ffffff',
  border: '1px solid #e2e8f0',
  borderRadius: '8px',
  fontSize: '14px',
  color: '#0f172a',
  resize: 'none',
  fontFamily: 'inherit',
  transition: 'all 0.2s',
  boxSizing: 'border-box',
};

const tagsContainerStyle = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '8px',
  marginTop: '12px',
};

const tagStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  padding: '4px 12px',
  backgroundColor: '#f1f5f9',
  color: '#475569',
  fontSize: '12px',
  fontWeight: '500',
  borderRadius: '999px',
  border: '1px solid #e2e8f0',
};

const tagCloseStyle = {
  marginLeft: '6px',
  backgroundColor: 'transparent',
  border: 'none',
  color: '#64748b',
  cursor: 'pointer',
  fontSize: '14px',
  padding: 0,
  display: 'flex',
  alignItems: 'center',
};

const modalFooterStyle = {
  padding: '20px 32px',
  borderTop: '1px solid #f1f5f9',
  backgroundColor: '#f8fafc',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
};

const stopMediaButtonStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  color: '#ef4444',
  backgroundColor: 'transparent',
  border: 'none',
  fontSize: '14px',
  fontWeight: '600',
  padding: '10px 16px',
  borderRadius: '8px',
  cursor: 'pointer',
  transition: 'all 0.2s',
};


const footerActionsStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
};

const cancelButtonStyle = {
  padding: '10px 24px',
  color: '#475569',
  backgroundColor: 'transparent',
  border: 'none',
  fontSize: '14px',
  fontWeight: '600',
  cursor: 'pointer',
  transition: 'color 0.2s',
};

const uploadPublishButtonStyle = (isUploading) => ({
  padding: '10px 32px',
  backgroundColor: isUploading ? '#94a3b8' : '#2563eb',
  color: '#ffffff',
  border: 'none',
  borderRadius: '8px',
  fontSize: '14px',
  fontWeight: '600',
  cursor: isUploading ? 'not-allowed' : 'pointer',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  boxShadow: '0 4px 6px rgba(37, 99, 235, 0.25)',
  transition: 'all 0.2s',
});


const modalOverlayStyle = {
  position: 'fixed',
  inset: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
};

const modalContentStyle = {
  backgroundColor: '#ffffff',
  borderRadius: '8px',
  maxWidth: '600px',
  width: '90%',
  maxHeight: '90vh',
  overflow: 'auto',
};

const modalHeaderStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '20px',
  borderBottom: '1px solid #e5e7eb',
};

const modalTitleStyle = {
  fontSize: '18px',
  fontWeight: '700',
  color: '#111827',
  margin: 0,
};

const modalCloseButtonStyle = {
  backgroundColor: 'transparent',
  border: 'none',
  fontSize: '20px',
  cursor: 'pointer',
  color: '#6b7280',
};

const containerStyle = {
  backgroundColor: '#ffffff',
  border: '1px solid #e5e7eb',
  borderRadius: '8px',
  padding: '24px',
  margin: '20px 40px',
};

const headerStyle = {
  fontSize: '24px',
  fontWeight: '700',
  color: '#111827',
  margin: 0,
};

const subHeaderStyle = {
  marginTop: '8px',
  marginBottom: '16px',
  color: '#6b7280',
  fontSize: '14px',
};

const projectLockStyle = {
  display: 'inline-flex',
  gap: '8px',
  alignItems: 'center',
  padding: '8px 12px',
  border: '1px solid #dbeafe',
  borderRadius: '999px',
  backgroundColor: '#eff6ff',
  marginBottom: '16px',
};

const projectLabelStyle = {
  fontSize: '12px',
  fontWeight: '600',
  color: '#1d4ed8',
  textTransform: 'uppercase',
};

const projectValueStyle = {
  fontSize: '13px',
  fontWeight: '700',
  color: '#1e3a8a',
};

const uploadCardStyle = {
  border: '1px solid #e5e7eb',
  borderRadius: '8px',
  padding: '16px',
  marginBottom: '16px',
  backgroundColor: '#fcfcfd',
};

const formGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
  gap: '14px',
};

const formGroupStyle = {
  marginBottom: '14px',
};

const labelStyle = {
  display: 'block',
  marginBottom: '6px',
  fontSize: '13px',
  fontWeight: '600',
  color: '#374151',
};

const inputStyle = {
  width: '100%',
  height: '40px',
  borderRadius: '6px',
  border: '1px solid #d1d5db',
  padding: '8px 10px',
  fontSize: '14px',
  boxSizing: 'border-box',
};

const fileInputStyle = {
  ...inputStyle,
  padding: '7px 10px',
};

const textAreaStyle = {
  width: '100%',
  minHeight: '88px',
  borderRadius: '6px',
  border: '1px solid #d1d5db',
  padding: '10px',
  fontSize: '14px',
  boxSizing: 'border-box',
  resize: 'vertical',
};

const primaryActionsStyle = {
  display: 'flex',
  gap: '10px',
  flexWrap: 'wrap',
};

const uploadButtonStyle = (isUploading) => ({
  border: 'none',
  borderRadius: '6px',
  padding: '11px 18px',
  fontSize: '14px',
  fontWeight: '700',
  color: '#ffffff',
  backgroundColor: isUploading ? '#9ca3af' : '#2563eb',
  cursor: isUploading ? 'not-allowed' : 'pointer',
});

const stopButtonStyle = {
  border: 'none',
  borderRadius: '6px',
  padding: '11px 18px',
  fontSize: '14px',
  fontWeight: '700',
  color: '#ffffff',
  backgroundColor: '#dc2626',
  cursor: 'pointer',
};

const statusMessageStyle = {
  marginTop: '12px',
  border: '1px solid #bfdbfe',
  backgroundColor: '#eff6ff',
  color: '#1d4ed8',
  borderRadius: '6px',
  padding: '10px',
  fontSize: '13px',
};

const errorMessageStyle = {
  marginTop: '12px',
  border: '1px solid #fecaca',
  backgroundColor: '#fee2e2',
  color: '#b91c1c',
  borderRadius: '6px',
  padding: '10px',
  fontSize: '13px',
};

const successMessageStyle = {
  marginTop: '12px',
  border: '1px solid #bbf7d0',
  backgroundColor: '#dcfce7',
  color: '#166534',
  borderRadius: '6px',
  padding: '10px',
  fontSize: '13px',
};

const tvQuickSelectCardStyle = {
  border: '1px solid #e5e7eb',
  borderRadius: '8px',
  padding: '16px',
  backgroundColor: '#ffffff',
  marginBottom: '16px',
};

const quickSelectTitleStyle = {
  fontSize: '14px',
  fontWeight: '700',
  color: '#111827',
  marginBottom: '10px',
};

const tvQuickGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
  gap: '8px',
};

const tvQuickButtonStyle = {
  border: '1px solid #d1d5db',
  borderRadius: '8px',
  padding: '10px',
  textAlign: 'left',
  backgroundColor: '#ffffff',
  cursor: 'pointer',
};

const selectedTvQuickButtonStyle = {
  ...tvQuickButtonStyle,
  border: '2px solid #c8a147',
  backgroundColor: '#fef9e7',
};

const tvQuickIdStyle = {
  fontSize: '13px',
  fontWeight: '700',
  color: '#111827',
};

const tvQuickMetaStyle = {
  fontSize: '12px',
  color: '#6b7280',
};

const libraryCardStyle = {
  border: '1px solid #e5e7eb',
  borderRadius: '8px',
  padding: '16px',
  backgroundColor: '#ffffff',
};

const libraryHeaderStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '12px',
  flexWrap: 'wrap',
  gap: '8px',
};

const libraryTitleStyle = {
  fontSize: '18px',
  fontWeight: '700',
  color: '#111827',
};

const libraryMetaStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  fontSize: '12px',
  color: '#6b7280',
};

const refreshButtonStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  border: '1px solid #e2e8f0',
  borderRadius: '12px',
  backgroundColor: '#ffffff',
  fontSize: '14px',
  fontWeight: '600',
  padding: '10px 16px',
  cursor: 'pointer',
  color: '#475569',
  transition: 'all 0.2s',
  boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
};

const refreshButtonHoverStyle = {
  backgroundColor: '#f8fafc',
  borderColor: '#cbd5e1',
  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
};

const refreshButtonDisabledStyle = {
  opacity: 0.5,
  cursor: 'not-allowed',
};

const scheduleInfoStyle = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '12px',
  fontSize: '12px',
  color: '#4b5563',
  marginBottom: '12px',
};

const scheduleWarningStyle = {
  color: '#b45309',
  backgroundColor: '#fef3c7',
  border: '1px solid #fde68a',
  borderRadius: '6px',
  padding: '4px 8px',
};

const sectionTabsStyle = {
  display: 'flex',
  gap: '8px',
  flexWrap: 'wrap',
  marginBottom: '12px',
};

const sectionTabStyle = {
  border: '1px solid #d1d5db',
  borderRadius: '999px',
  backgroundColor: '#ffffff',
  color: '#374151',
  padding: '7px 12px',
  fontSize: '12px',
  fontWeight: '600',
  cursor: 'pointer',
};

const activeSectionTabStyle = {
  ...sectionTabStyle,
  backgroundColor: '#fef9e7',
  border: '1px solid #c8a147',
  color: '#8b6919',
};

const loadingStyle = {
  padding: '20px',
  textAlign: 'center',
  color: '#6b7280',
  fontSize: '14px',
};

const emptyStateStyle = {
  border: '1px dashed #d1d5db',
  borderRadius: '6px',
  padding: '20px',
  textAlign: 'center',
  color: '#6b7280',
  fontSize: '14px',
};

const itemListStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
  gap: '12px',
};

const itemCardStyle = {
  border: '1px solid #e5e7eb',
  borderRadius: '8px',
  padding: '12px',
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  minHeight: '360px',
};

const previewContainerStyle = {
  width: '100%',
  height: '150px',
  borderRadius: '6px',
  overflow: 'hidden',
  backgroundColor: '#f3f4f6',
  border: '1px solid #e5e7eb',
};

const previewMediaStyle = {
  width: '100%',
  height: '100%',
  objectFit: 'cover',
};

const previewFallbackStyle = {
  width: '100%',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)',
};

const previewFallbackTextStyle = {
  fontSize: '12px',
  fontWeight: '700',
  color: '#6b7280',
  letterSpacing: '0.4px',
};

const itemInfoStyle = {
  flex: 1,
};

const itemTitleStyle = {
  fontSize: '14px',
  fontWeight: '700',
  color: '#111827',
  marginBottom: '4px',
};

const itemMetaStyle = {
  fontSize: '12px',
  color: '#6b7280',
  marginBottom: '4px',
};

const itemUrlStyle = {
  fontSize: '12px',
  color: '#374151',
  wordBreak: 'break-all',
};

const scheduleEditorStyle = {
  borderTop: '1px solid #e5e7eb',
  paddingTop: '10px',
  marginTop: '4px',
};

const scheduleInputGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  gap: '8px',
  marginBottom: '8px',
};

const miniLabelStyle = {
  display: 'block',
  fontSize: '11px',
  color: '#6b7280',
  marginBottom: '4px',
  fontWeight: '600',
};

const miniInputStyle = {
  width: '100%',
  height: '34px',
  borderRadius: '6px',
  border: '1px solid #d1d5db',
  padding: '6px 8px',
  fontSize: '12px',
  boxSizing: 'border-box',
};

const slotInfoStyle = {
  fontSize: '11px',
  color: '#1d4ed8',
  backgroundColor: '#eff6ff',
  border: '1px solid #bfdbfe',
  borderRadius: '6px',
  padding: '6px 8px',
  marginBottom: '8px',
};

const itemActionsStyle = {
  display: 'flex',
  gap: '6px',
  flexWrap: 'wrap',
};

const playButtonStyle = {
  border: 'none',
  borderRadius: '6px',
  padding: '8px 10px',
  fontSize: '12px',
  fontWeight: '700',
  color: '#ffffff',
  backgroundColor: '#10b981',
  cursor: 'pointer',
};

const scheduleButtonStyle = {
  border: 'none',
  borderRadius: '6px',
  padding: '8px 10px',
  fontSize: '12px',
  fontWeight: '700',
  color: '#ffffff',
  backgroundColor: '#2563eb',
  cursor: 'pointer',
};

const removeScheduleButtonStyle = {
  border: 'none',
  borderRadius: '6px',
  padding: '8px 10px',
  fontSize: '12px',
  fontWeight: '700',
  color: '#ffffff',
  backgroundColor: '#ef4444',
  cursor: 'pointer',
};

const openLinkStyle = {
  border: '1px solid #d1d5db',
  borderRadius: '6px',
  padding: '7px 10px',
  fontSize: '12px',
  fontWeight: '700',
  color: '#111827',
  backgroundColor: '#ffffff',
  textDecoration: 'none',
};

const actionMessageStyle = {
  marginTop: '12px',
  border: '1px solid #e5e7eb',
  backgroundColor: '#f9fafb',
  color: '#374151',
  borderRadius: '6px',
  padding: '10px',
  fontSize: '13px',
};

export default WebsiteContentUploader;
